import { mkdir, readFile, writeFile } from "node:fs/promises";
import path from "node:path";
import OpenAI from "openai";
import type {
  ChatCompletion,
  ChatCompletionCreateParamsNonStreaming,
  ChatCompletionCreateParamsStreaming,
  ChatCompletionMessageParam,
} from "openai/resources/chat/completions";
import { Granular, EnvironmentSession } from "./client";
import type { ToolWithHandler } from "./session";
import {
  buildContinuationInstruction,
  buildGranularAgentSystemPrompt,
  createHarnessVerifierSnapshot,
  evaluateContinuation,
  getCurrentClosureId,
  projectConversationReferentFocus,
  projectConversationReferentSummary,
  projectHeapSummary,
  projectLoopSummary,
  projectSessionFileSummary,
  projectWorkflowFocus,
  projectWorkflowSummary,
  reviewGeneratedJobCode,
  resolveHarnessTemplate,
  consumeGranularReasoningOnlyChunk,
  type GeneratedJobCodeIssue,
  type GranularAgentExecutionCheckpoint,
  type GranularAgentToolInfo,
  type BuildGranularAgentSystemPromptInput,
  type HarnessControllerBudgets,
  type HarnessRenderedContinuation,
  type HarnessRenderedPrompt,
  type HarnessVerifierSnapshot,
} from "./agent-harness";
import { resolveJobPresentation } from "./job-presentation";
import { readSessionFeedSnapshot, type FeedItem } from "./feed";
import { normalizePrompt, resolvePromptAnswer } from "./prompt-utils";
import {
  calculateOpenAITokenSpend,
  type OpenAITokenSpend,
} from "./openai-usage";
import type { GranularSpendContext } from "./spend";
import type {
  ConnectOptions,
  CreateEnvironmentData,
  GranularOptions,
  ManifestContent,
  Prompt,
  RecordObjectOptions,
  SessionHeapEntry,
  SessionHeapList,
  SessionHeapSnapshot,
  SessionHeapVariable,
} from "./types";

export type EvalMatcher = string | RegExp;

export interface AgentEvalPromptInteraction {
  promptId: string;
  type: string;
  title: string;
  message: string;
  answer: unknown;
}

export interface AgentEvalGenerationInput {
  systemPrompt: string;
  history: Array<{ role: "user" | "assistant"; content: string }>;
  request: string;
  attempt: number;
  tools?: GranularAgentToolInfo[];
  repairIssues?: GeneratedJobCodeIssue[];
  onTextDelta?: (delta: string) => void | Promise<void>;
  onReplyDelta?: (delta: string) => void | Promise<void>;
  onCodeDelta?: (delta: string) => void | Promise<void>;
  usageContext?: GranularSpendContext;
}

export type AgentEvalPromptRenderer = (
  input: BuildGranularAgentSystemPromptInput,
) => HarnessRenderedPrompt;

export type AgentEvalContinuationRenderer = (
  resultPreview?: string,
) => HarnessRenderedContinuation;

export interface AgentEvalGenerationOutput {
  reply?: string;
  code?: string;
  raw?: unknown;
  generationAttempts?: AgentEvalGenerationAttempt[];
}

export interface AgentEvalGenerationAttempt {
  attempt: number;
  request: string;
  repairIssues?: GeneratedJobCodeIssue[];
  reply?: string;
  code?: string;
  raw?: unknown;
}

export interface AgentEvalTokenUsage {
  calls: number;
  inputTokens: number;
  cachedInputTokens: number;
  uncachedInputTokens: number;
  outputTokens: number;
  totalTokens: number;
  inputCostUsd: number;
  cachedInputCostUsd: number;
  outputCostUsd: number;
  totalCostUsd: number;
  missingUsageCalls: number;
}

export interface AgentEvalOpenAIUsageEvent extends OpenAITokenSpend {
  spendEventId?: string;
  source: "openai";
  lineItemType: "llm_tokens";
  operation: "chat.completions";
  requestId?: string | null;
  usageContext?: GranularSpendContext;
  rawUsage: unknown;
}

export type AgentEvalTurnGenerator = (
  input: AgentEvalGenerationInput,
) => Promise<AgentEvalGenerationOutput>;

export interface ScriptedPromptRule {
  type?: Prompt["type"];
  when?: EvalMatcher | EvalMatcher[];
  answer:
    | unknown
    | ((input: {
        prompt: Prompt;
        history: AgentEvalPromptInteraction[];
      }) => unknown | Promise<unknown>);
}

export type AgentEvalPromptResponder = (input: {
  prompt: Prompt;
  history: AgentEvalPromptInteraction[];
}) => Promise<unknown>;

export interface AgentEvalExpectations {
  replyIncludes?: EvalMatcher[];
  replyExcludes?: EvalMatcher[];
  actionIncludes?: EvalMatcher[];
  actionExcludes?: EvalMatcher[];
  codeIncludes?: EvalMatcher[];
  codeExcludes?: EvalMatcher[];
  behaviorBuckets?: string[];
  actions?: {
    required?: EvalMatcher[];
    forbidden?: EvalMatcher[];
  };
  prompts?: {
    requiredChoiceWhenAmbiguous?: boolean;
    requiredConfirmationBefore?: EvalMatcher[];
    forbiddenPromptTypes?: string[];
  };
  presentation?: {
    mustMention?: EvalMatcher[];
    mustNotMention?: EvalMatcher[];
    mustDisplayOrSave?: EvalMatcher[];
  };
}

export interface AgentEvalScenarioFilePromptRule {
  type?: Prompt["type"];
  when?: string | string[];
  answer: unknown;
}

export interface AgentEvalScenarioFileStep {
  id?: string;
  request: string;
  behaviorBuckets?: string[];
  human?: AgentEvalScenarioFilePromptRule[];
  inspect?: AgentEvalScenarioFileInspection | AgentEvalScenarioFileInspection[];
  expect?: {
    replyIncludes?: string[];
    replyExcludes?: string[];
    actionIncludes?: string[];
    actionExcludes?: string[];
    codeIncludes?: string[];
    codeExcludes?: string[];
    behaviorBuckets?: string[];
    actions?: {
      required?: string[];
      forbidden?: string[];
    };
    prompts?: AgentEvalExpectations["prompts"];
    presentation?: {
      mustMention?: string[];
      mustNotMention?: string[];
      mustDisplayOrSave?: string[];
    };
  };
}

export interface AgentEvalScenarioFileInspection {
  code: string;
  includes?: string[];
  excludes?: string[];
}

export interface AgentEvalScenarioFileScenario {
  id: string;
  description?: string;
  request?: string;
  behaviorBuckets?: string[];
  human?: AgentEvalScenarioFilePromptRule[];
  steps?: AgentEvalScenarioFileStep[];
  prepareRecords?: RecordObjectOptions[];
  verify?: AgentEvalScenarioFileInspection;
  inspect?: AgentEvalScenarioFileInspection | AgentEvalScenarioFileInspection[];
  expect?: AgentEvalScenarioFileStep["expect"];
}

export interface AgentEvalScenarioFileEnvironmentTarget {
  mode: "environment";
}

export interface AgentEvalScenarioFileSelfContainedTarget {
  mode: "self-contained";
  sandboxName?: string;
  tag?: string;
  userId?: string;
  name?: string;
  email?: string;
  permissions?: string[];
  effectPreset?: string;
  extraRecords?: RecordObjectOptions[];
}

export interface AgentEvalScenarioFile {
  id: string;
  title?: string;
  description?: string;
  domainDocumentation?: string;
  manifest?: ManifestContent;
  target?:
    | AgentEvalScenarioFileSelfContainedTarget
    | AgentEvalScenarioFileEnvironmentTarget;
  scenarios: AgentEvalScenarioFileScenario[];
}

export interface AgentEvalCheckContext {
  conversation: AgentEvalConversation;
  environment: EnvironmentSession;
  turnDir: string;
  request: string;
  responseText: string;
  terminalKind: "reply" | "closure";
  finalCode?: string;
  actionSummary: string[];
  promptInteractions: AgentEvalPromptInteraction[];
  result: unknown;
  heap: SessionHeapSnapshot;
  openPrompts: Prompt[];
  liveDoc: Record<string, unknown>;
  inspect: (code: string) => Promise<unknown>;
  assertMatches: (
    label: string,
    text: string,
    includes?: EvalMatcher[],
    excludes?: EvalMatcher[],
  ) => void;
}

export type AgentEvalCheck = (
  context: AgentEvalCheckContext,
) => Promise<void> | void;

export interface AgentEvalInspection {
  code: string;
  includes?: EvalMatcher[];
  excludes?: EvalMatcher[];
  check?: AgentEvalCheck;
}

export interface AgentEvalSetupContext {
  conversation: AgentEvalConversation;
  environment: EnvironmentSession;
  turnDir: string;
}

export interface AgentEvalSetup {
  manifest?: ManifestContent;
  records?: RecordObjectOptions[];
  effects?: ToolWithHandler[];
  run?: (input: AgentEvalSetupContext) => Promise<void>;
}

export interface AgentEvalPrepareContext {
  conversation: AgentEvalConversation;
  environment: EnvironmentSession;
  turnDir: string;
}

export interface AgentEvalStep {
  id?: string;
  title?: string;
  request: string;
  behaviorBuckets?: string[];
  setup?: AgentEvalSetup;
  human?: AgentEvalPromptResponder;
  expect?: AgentEvalExpectations;
  inspect?: AgentEvalInspection | AgentEvalInspection[];
  check?: AgentEvalCheck | AgentEvalCheck[];
  maxIterations?: number;
  autoAnswerPrompts?: boolean;
}

export interface AgentEvalScenario {
  id: string;
  description?: string;
  jobSource?: "llm" | "hardcoded" | "mixed";
  behaviorBuckets?: string[];
  request?: string;
  steps?: AgentEvalStep[];
  setup?: AgentEvalSetup;
  prepareRecords?: RecordObjectOptions[];
  prepareTools?: ToolWithHandler[];
  prepare?: (input: AgentEvalPrepareContext) => Promise<void>;
  human?: AgentEvalPromptResponder;
  expect?: AgentEvalExpectations;
  verify?: AgentEvalInspection;
  inspect?: AgentEvalInspection | AgentEvalInspection[];
  check?: AgentEvalCheck | AgentEvalCheck[];
  maxIterations?: number;
}

export interface AgentEvalStepResult {
  id: string;
  request: string;
  responseText: string;
  terminalKind: "reply" | "closure";
  finalCode?: string;
  actionSummary: string[];
  promptInteractions: AgentEvalPromptInteraction[];
  inspectionResults: unknown[];
  assertions?: AgentEvalAssertion[];
  turnDir: string;
}

export interface AgentEvalAssertion {
  id: string;
  label: string;
  status: "passed" | "failed";
  message?: string;
}

export interface AgentEvalResult {
  scenario: AgentEvalScenario;
  status: "passed" | "failed";
  responseText: string;
  terminalKind: "reply" | "closure";
  finalCode?: string;
  actionSummary: string[];
  promptInteractions: AgentEvalPromptInteraction[];
  verification: unknown | null;
  assertions?: AgentEvalAssertion[];
  tokenUsage?: AgentEvalTokenUsage;
  steps?: AgentEvalStepResult[];
  turnDir: string;
  error?: string;
}

export interface AgentEvalSuiteResult {
  artifactDir: string;
  results: AgentEvalResult[];
}

export interface AgentEvalHarnessOptions {
  granular: Granular;
  environmentId?: string;
  openEnvironment?: (input: {
    label: string;
    clientId: string;
  }) => Promise<EnvironmentSession>;
  generator: AgentEvalTurnGenerator;
  artifactBaseDir?: string;
  suiteName?: string;
  controllerBudgets?: HarnessControllerBudgets;
  chatTimeoutMs?: number;
  jobTimeoutMs?: number;
  pollIntervalMs?: number;
  local?: boolean;
  localTurnReportBaseDir?: string;
  harnessTemplateId?: string;
  promptRenderer?: AgentEvalPromptRenderer;
  continuationRenderer?: AgentEvalContinuationRenderer;
  onProgress?: (event: AgentEvalProgressEvent) => void | Promise<void>;
}

export type AgentEvalProgressStatus = "info" | "running" | "passed" | "failed";

export interface AgentEvalProgressEvent {
  id: string;
  timestamp: string;
  phase:
    | "scenario"
    | "setup"
    | "step"
    | "prompt"
    | "generation"
    | "job"
    | "interaction"
    | "continuation"
    | "assertion"
    | "inspection"
    | "check";
  status: AgentEvalProgressStatus;
  scenarioId?: string;
  stepId?: string;
  iteration?: number;
  jobId?: string;
  templateId?: string;
  templateVersion?: string;
  title: string;
  message?: string;
  data?: unknown;
}

export interface AgentEvalConversation {
  label: string;
  environment: EnvironmentSession;
  history: AgentEvalHistoryEntry[];
  promptEvents: PendingPromptEvent[];
  artifactDir: string;
  turnCount: number;
  logTurns: AgentEvalSessionLogTurn[];
}

interface AgentEvalSessionLogIteration {
  iteration: number;
  request: string;
  systemPrompt: string;
  generationDurationMs?: number;
  templateId?: string;
  templateVersion?: string;
  templateHash?: string;
  promptInstanceHash?: string;
  generationReply?: string;
  generatedCode?: string;
  rawGeneration?: unknown;
  generationAttempts?: AgentEvalGenerationAttempt[];
  tokenUsage?: AgentEvalTokenUsage;
  responseText?: string;
  terminalKind?: "reply" | "closure";
  actionSummary?: string[];
  promptInteractions?: AgentEvalPromptInteraction[];
  continuation?: unknown;
  result?: unknown;
  error?: string;
}

interface AgentEvalSessionLogTurn {
  turnNumber: number;
  turnId: string;
  request: string;
  turnDir: string;
  iterations: AgentEvalSessionLogIteration[];
  completed?: {
    responseText: string;
    terminalKind: "reply" | "closure";
    actionSummary: string[];
    promptInteractions: AgentEvalPromptInteraction[];
    result?: unknown;
  };
  error?: string;
}

export interface AgentEvalPendingTurn {
  conversation: AgentEvalConversation;
  request: string;
  turnDir: string;
  boundaryTimestamp: number;
  finalCode: string;
  stdout: string[];
  stderr: string[];
  prompts: Prompt[];
  promptInteractions: AgentEvalPromptInteraction[];
  job: {
    id: string;
    result: Promise<unknown>;
    on: (event: string, handler: (data: unknown) => void) => void;
  };
}

export interface AgentEvalCompletedTurn {
  conversation: AgentEvalConversation;
  request: string;
  turnDir: string;
  responseText: string;
  terminalKind: "reply" | "closure";
  finalCode?: string;
  actionSummary: string[];
  promptInteractions: AgentEvalPromptInteraction[];
  verification: unknown | null;
  result: unknown;
}

interface AgentEvalHistoryEntry {
  role: "user" | "assistant";
  content: string;
  code?: string;
  jobStatus?: string;
  jobResultPreview?: string;
  error?: string;
}

interface PendingPromptEvent {
  prompt: Prompt;
  receivedAt: number;
}

const DEFAULT_CONTROLLER_BUDGETS: Required<HarnessControllerBudgets> = {
  maxIterations: 6,
  maxNoProgressIterations: 2,
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function slugify(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 64) || "item"
  );
}

function timestampId(now = new Date()): string {
  return now.toISOString().replace(/[:.]/g, "-");
}

function matcherToString(matcher: EvalMatcher): string {
  return typeof matcher === "string" ? matcher : matcher.toString();
}

function matchesPattern(text: string, matcher: EvalMatcher): boolean {
  if (typeof matcher === "string") return text.includes(matcher);
  return matcher.test(text);
}

function matcherFromConfig(value: string): EvalMatcher {
  const match = value.match(/^\/([\s\S]*)\/([dgimsuvy]*)$/);
  if (!match) return value;
  return new RegExp(match[1] || "", match[2] || "");
}

function matchersFromConfig(values?: string[]): EvalMatcher[] | undefined {
  if (!values?.length) return undefined;
  return values.map((value) => matcherFromConfig(value));
}

function expectationsFromConfig(
  input?: AgentEvalScenarioFileStep["expect"],
): AgentEvalExpectations | undefined {
  if (!input) return undefined;
  return {
    replyIncludes: matchersFromConfig(input.replyIncludes),
    replyExcludes: matchersFromConfig(input.replyExcludes),
    actionIncludes: matchersFromConfig(input.actionIncludes),
    actionExcludes: matchersFromConfig(input.actionExcludes),
    codeIncludes: matchersFromConfig(input.codeIncludes),
    codeExcludes: matchersFromConfig(input.codeExcludes),
    behaviorBuckets: input.behaviorBuckets,
    actions: input.actions
      ? {
          required: matchersFromConfig(input.actions.required),
          forbidden: matchersFromConfig(input.actions.forbidden),
        }
      : undefined,
    prompts: input.prompts
      ? {
          ...input.prompts,
          requiredConfirmationBefore: matchersFromConfig(
            input.prompts.requiredConfirmationBefore as string[] | undefined,
          ),
        }
      : undefined,
    presentation: input.presentation
      ? {
          mustMention: matchersFromConfig(input.presentation.mustMention),
          mustNotMention: matchersFromConfig(input.presentation.mustNotMention),
          mustDisplayOrSave: matchersFromConfig(
            input.presentation.mustDisplayOrSave,
          ),
        }
      : undefined,
  };
}

function promptResponderFromConfig(
  rules?: AgentEvalScenarioFilePromptRule[],
): AgentEvalPromptResponder | undefined {
  if (!rules?.length) return undefined;
  return createHumanResponder(
    rules.map((rule) => ({
      type: rule.type,
      when: Array.isArray(rule.when)
        ? rule.when.map((matcher) => matcherFromConfig(matcher))
        : rule.when
          ? matcherFromConfig(rule.when)
          : undefined,
      answer: rule.answer,
    })),
  );
}

function inspectionFromConfig(
  inspection?: AgentEvalScenarioFileInspection,
): AgentEvalInspection | undefined {
  if (!inspection) return undefined;
  return {
    code: inspection.code,
    includes: matchersFromConfig(inspection.includes),
    excludes: matchersFromConfig(inspection.excludes),
  };
}

function inspectionsFromConfig(
  inspections?:
    | AgentEvalScenarioFileInspection
    | AgentEvalScenarioFileInspection[],
): AgentEvalInspection | AgentEvalInspection[] | undefined {
  if (!inspections) return undefined;
  if (Array.isArray(inspections)) {
    return inspections
      .map((inspection) => inspectionFromConfig(inspection))
      .filter((inspection): inspection is AgentEvalInspection =>
        Boolean(inspection),
      );
  }
  return inspectionFromConfig(inspections);
}

export function scenariosFromAgentEvalFile(
  file: AgentEvalScenarioFile,
): AgentEvalScenario[] {
  return file.scenarios.map((scenario) => ({
    id: scenario.id,
    description: scenario.description,
    request: scenario.request,
    behaviorBuckets: scenario.behaviorBuckets,
    human: promptResponderFromConfig(scenario.human),
    prepareRecords: scenario.prepareRecords,
    expect: expectationsFromConfig(scenario.expect),
    verify: inspectionFromConfig(scenario.verify),
    inspect: inspectionsFromConfig(scenario.inspect),
    steps: scenario.steps?.map((step) => ({
      id: step.id,
      request: step.request,
      behaviorBuckets: step.behaviorBuckets || scenario.behaviorBuckets,
      human: promptResponderFromConfig(step.human || scenario.human),
      expect: expectationsFromConfig(step.expect || scenario.expect),
      inspect: inspectionsFromConfig(step.inspect),
    })),
  }));
}

export async function loadAgentEvalScenarioFile(
  filePath: string,
): Promise<AgentEvalScenario[]> {
  const raw = JSON.parse(
    await readFile(filePath, "utf8"),
  ) as AgentEvalScenarioFile;
  return scenariosFromAgentEvalFile(raw);
}

function assertMatches(
  label: string,
  text: string,
  includes: EvalMatcher[] = [],
  excludes: EvalMatcher[] = [],
) {
  for (const matcher of includes) {
    if (!matchesPattern(text, matcher)) {
      throw new Error(
        `${label} did not match ${matcherToString(matcher)}:\n${text}`,
      );
    }
  }
  for (const matcher of excludes) {
    if (matchesPattern(text, matcher)) {
      throw new Error(
        `${label} matched forbidden ${matcherToString(matcher)}:\n${text}`,
      );
    }
  }
}

function buildArtifactDir(
  baseDir?: string,
  suiteName = "granular-agent-evals",
): string {
  return path.join(
    baseDir || path.join(process.cwd(), "test-artifacts"),
    suiteName,
    timestampId(),
  );
}

export function createTimestampedArtifactDirectory(options?: {
  baseDir?: string;
  suiteName?: string;
}): string {
  return buildArtifactDir(options?.baseDir, options?.suiteName);
}

function asArray<T>(value: T | T[] | undefined): T[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function progressEvent(
  input: Omit<AgentEvalProgressEvent, "id" | "timestamp"> & {
    id?: string;
  },
): AgentEvalProgressEvent {
  return {
    ...input,
    id:
      input.id ||
      [
        input.scenarioId || "suite",
        input.stepId || input.phase,
        input.iteration ? `iteration-${input.iteration}` : "",
        input.jobId || "",
        input.title,
        Date.now(),
      ]
        .filter(Boolean)
        .join(":"),
    timestamp: new Date().toISOString(),
  };
}

async function emitProgress(
  handler: AgentEvalHarnessOptions["onProgress"],
  event: Omit<AgentEvalProgressEvent, "id" | "timestamp"> & { id?: string },
) {
  await handler?.(progressEvent(event));
}

const GRANULAR_AGENT_TOKEN_PRICING_USD_PER_MILLION = {
  input: 0.2,
  cachedInput: 0.02,
  output: 1.2,
};

function emptyTokenUsage(): AgentEvalTokenUsage {
  return {
    calls: 0,
    inputTokens: 0,
    cachedInputTokens: 0,
    uncachedInputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    inputCostUsd: 0,
    cachedInputCostUsd: 0,
    outputCostUsd: 0,
    totalCostUsd: 0,
    missingUsageCalls: 0,
  };
}

function numberField(record: Record<string, unknown> | null, key: string) {
  const value = record?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function calculateTokenCost(input: {
  model?: string;
  uncachedInputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
}) {
  const spend = input.model
    ? calculateOpenAITokenSpend(input.model, {
        input_tokens: input.uncachedInputTokens + input.cachedInputTokens,
        output_tokens: input.outputTokens,
        input_tokens_details: { cached_tokens: input.cachedInputTokens },
      })
    : null;
  const pricing = spend
    ? {
        input: spend.inputPricePerMillionMicros / 1_000_000,
        cachedInput: spend.cachedInputPricePerMillionMicros / 1_000_000,
        output: spend.outputPricePerMillionMicros / 1_000_000,
      }
    : GRANULAR_AGENT_TOKEN_PRICING_USD_PER_MILLION;
  const inputCostUsd = (input.uncachedInputTokens * pricing.input) / 1_000_000;
  const cachedInputCostUsd =
    (input.cachedInputTokens * pricing.cachedInput) / 1_000_000;
  const outputCostUsd = (input.outputTokens * pricing.output) / 1_000_000;
  return {
    inputCostUsd,
    cachedInputCostUsd,
    outputCostUsd,
    totalCostUsd: inputCostUsd + cachedInputCostUsd + outputCostUsd,
  };
}

function extractTokenUsageFromRaw(raw: unknown): AgentEvalTokenUsage | null {
  const usage = asRecord(asRecord(raw)?.usage);
  if (!usage) return null;
  const model =
    typeof asRecord(raw)?.model === "string"
      ? (asRecord(raw)?.model as string)
      : undefined;

  const inputTokens =
    numberField(usage, "prompt_tokens") || numberField(usage, "input_tokens");
  const outputTokens =
    numberField(usage, "completion_tokens") ||
    numberField(usage, "output_tokens");
  const details =
    asRecord(usage.prompt_tokens_details) ||
    asRecord(usage.input_tokens_details);
  const cachedInputTokens = Math.min(
    inputTokens,
    numberField(details, "cached_tokens") ||
      numberField(details, "cached_input_tokens"),
  );
  const uncachedInputTokens = Math.max(inputTokens - cachedInputTokens, 0);
  const totalTokens =
    numberField(usage, "total_tokens") || inputTokens + outputTokens;
  const costs = calculateTokenCost({
    model,
    uncachedInputTokens,
    cachedInputTokens,
    outputTokens,
  });

  return {
    calls: 1,
    inputTokens,
    cachedInputTokens,
    uncachedInputTokens,
    outputTokens,
    totalTokens,
    ...costs,
    missingUsageCalls: 0,
  };
}

function addTokenUsage(
  aggregate: AgentEvalTokenUsage,
  usage: AgentEvalTokenUsage | null | undefined,
): AgentEvalTokenUsage {
  if (!usage) {
    return {
      ...aggregate,
      missingUsageCalls: aggregate.missingUsageCalls + 1,
    };
  }
  const inputTokens = aggregate.inputTokens + usage.inputTokens;
  const cachedInputTokens =
    aggregate.cachedInputTokens + usage.cachedInputTokens;
  const uncachedInputTokens =
    aggregate.uncachedInputTokens + usage.uncachedInputTokens;
  const outputTokens = aggregate.outputTokens + usage.outputTokens;
  const costs = calculateTokenCost({
    uncachedInputTokens,
    cachedInputTokens,
    outputTokens,
  });
  return {
    calls: aggregate.calls + usage.calls,
    inputTokens,
    cachedInputTokens,
    uncachedInputTokens,
    outputTokens,
    totalTokens: aggregate.totalTokens + usage.totalTokens,
    ...costs,
    missingUsageCalls: aggregate.missingUsageCalls + usage.missingUsageCalls,
  };
}

function aggregateTokenUsage(
  usages: Array<AgentEvalTokenUsage | null | undefined>,
): AgentEvalTokenUsage {
  return usages.reduce<AgentEvalTokenUsage>(
    (aggregate, usage) => addTokenUsage(aggregate, usage),
    emptyTokenUsage(),
  );
}

function aggregateConversationTokenUsage(
  conversation: AgentEvalConversation,
): AgentEvalTokenUsage {
  return aggregateTokenUsage(
    (conversation.logTurns || []).flatMap((turn) =>
      turn.iterations.map((iteration) => iteration.tokenUsage),
    ),
  );
}

function tokenUsageForGenerationOutput(
  generation: AgentEvalGenerationOutput,
): AgentEvalTokenUsage {
  const attempts = generation.generationAttempts?.length
    ? generation.generationAttempts
    : [{ raw: generation.raw }];
  return aggregateTokenUsage(
    attempts.map((attempt) => extractTokenUsageFromRaw(attempt.raw)),
  );
}

function formatUsd(value: number): string {
  return `$${value.toFixed(6)}`;
}

function formatTokenUsage(usage: AgentEvalTokenUsage | undefined): string[] {
  if (!usage || (usage.calls === 0 && usage.missingUsageCalls === 0)) {
    return ["- LLM calls with usage data: 0", "- Total cost: $0.000000"];
  }
  return [
    `- LLM calls with usage data: ${usage.calls}`,
    `- LLM calls missing usage data: ${usage.missingUsageCalls}`,
    `- Input tokens: ${usage.inputTokens}`,
    `- Cached input tokens: ${usage.cachedInputTokens}`,
    `- Uncached input tokens: ${usage.uncachedInputTokens}`,
    `- Output tokens: ${usage.outputTokens}`,
    `- Total tokens: ${usage.totalTokens}`,
    `- Input cost: ${formatUsd(usage.inputCostUsd)}`,
    `- Cached input cost: ${formatUsd(usage.cachedInputCostUsd)}`,
    `- Output cost: ${formatUsd(usage.outputCostUsd)}`,
    `- Total cost: ${formatUsd(usage.totalCostUsd)}`,
    `- Pricing fallback: gpt-5.6-luna at $${GRANULAR_AGENT_TOKEN_PRICING_USD_PER_MILLION.input}/M input, $${GRANULAR_AGENT_TOKEN_PRICING_USD_PER_MILLION.cachedInput}/M cached input, $${GRANULAR_AGENT_TOKEN_PRICING_USD_PER_MILLION.output}/M output. Per-call model pricing is used when available.`,
  ];
}

function getJobFeedItems(
  liveDoc: Record<string, unknown>,
  jobId: string,
): FeedItem[] {
  const snapshot = readSessionFeedSnapshot(liveDoc);
  if (snapshot.error) throw snapshot.error;
  return snapshot.tail.filter((item) => item.source?.jobId === jobId);
}

async function collectJobFeedItems(
  environment: EnvironmentSession,
  jobId: string,
): Promise<FeedItem[]> {
  const items: FeedItem[] = [];
  let afterSequence = 0;
  for (let pageCount = 0; pageCount < 10_000; pageCount += 1) {
    const page = await environment.feed.list({
      afterSequence,
      limit: 500,
    });
    if (page.items[0] && page.items[0].sequence !== afterSequence + 1) {
      throw new Error(
        `Canonical eval feed has a gap after sequence ${afterSequence}.`,
      );
    }
    items.push(...page.items.filter((item) => item.source?.jobId === jobId));
    if (!page.hasMoreAfter) return items;
    const nextSequence = page.items.at(-1)?.sequence;
    if (!nextSequence || nextSequence <= afterSequence) {
      throw new Error(
        `Canonical eval feed stopped after sequence ${afterSequence}.`,
      );
    }
    afterSequence = nextSequence;
  }
  throw new Error("Canonical eval feed exceeded its page limit.");
}

function buildScenarioSteps(scenario: AgentEvalScenario): AgentEvalStep[] {
  if (scenario.steps?.length) {
    return scenario.steps;
  }

  if (!scenario.request) {
    throw new Error(
      `Scenario ${scenario.id} must provide either request or steps`,
    );
  }

  const compatibilityStep: AgentEvalStep = {
    id: scenario.id,
    request: scenario.request,
    human: scenario.human,
    expect: scenario.expect,
    inspect: [...asArray(scenario.inspect), ...asArray(scenario.verify)],
    check: scenario.check,
    maxIterations: scenario.maxIterations,
    setup: {
      records: scenario.prepareRecords,
      effects: scenario.prepareTools,
      run: scenario.prepare,
    },
  };

  return [compatibilityStep];
}

function buildAssistantHistoryContent(entry: AgentEvalHistoryEntry): string {
  if (!entry.code) return entry.content;
  const parts: string[] = [];
  if (entry.content.trim()) parts.push(`[Assistant reply]\n${entry.content}`);
  if (entry.jobStatus) parts.push(`[Job status]\n${entry.jobStatus}`);
  if (entry.jobResultPreview)
    parts.push(`[Job result]\n${entry.jobResultPreview}`);
  if (entry.error) parts.push(`[Job error]\n${entry.error}`);
  return parts.join("\n\n") || entry.content;
}

function buildHistory(entries: AgentEvalHistoryEntry[]) {
  return entries.reduce<Array<{ role: "user" | "assistant"; content: string }>>(
    (history, entry) => {
      if (entry.role === "user") {
        if (entry.content.trim())
          history.push({ role: "user", content: entry.content });
        return history;
      }
      const content = buildAssistantHistoryContent(entry).trim();
      if (content) history.push({ role: "assistant", content });
      return history;
    },
    [],
  );
}

function getOpenPromptsFromDoc(
  liveDoc: Record<string, unknown> | null,
): Prompt[] {
  const jobsById = asRecord(asRecord(liveDoc?.jobs)?.byId) || {};
  const prompts: Prompt[] = [];
  for (const job of Object.values(jobsById)) {
    const promptRecords = asRecord(asRecord(job)?.prompts) || {};
    for (const raw of Object.values(promptRecords)) {
      const record = asRecord(raw);
      if (
        !record ||
        record.status !== "open" ||
        typeof record.promptId !== "string"
      )
        continue;
      const prompt = normalizePrompt({
        promptId: record.promptId,
        kind: record.kind,
        type: record.type,
        title: record.title,
        message: record.message,
        options: record.options,
        defaultValue: record.defaultValue,
        placeholder: record.placeholder,
        allowEmpty: record.allowEmpty,
        metadata: record.metadata,
      });
      if (prompt) prompts.push(prompt);
    }
  }
  return prompts;
}

function filterPromptsByBoundary(
  liveDoc: Record<string, unknown> | null,
  prompts: Prompt[],
  boundaryTimestamp: number,
): Prompt[] {
  const jobsById = asRecord(asRecord(liveDoc?.jobs)?.byId) || {};
  return prompts.filter((prompt) => {
    for (const jobRecord of Object.values(jobsById)) {
      const promptsById = asRecord(asRecord(jobRecord)?.prompts) || {};
      const promptRecord = asRecord(promptsById[prompt.id]);
      const openedAt = Number(promptRecord?.openedAt) || 0;
      if (openedAt >= boundaryTimestamp) return true;
    }
    return false;
  });
}

export function createScriptedPromptResponder(
  rules: ScriptedPromptRule[],
  fallback?: AgentEvalPromptResponder,
): AgentEvalPromptResponder {
  const responder: AgentEvalPromptResponder = async ({ prompt, history }) => {
    const promptText = `${prompt.title || ""}\n${prompt.message || ""}`;
    for (const rule of rules) {
      if (rule.type && rule.type !== prompt.type) continue;
      const matchers = Array.isArray(rule.when)
        ? rule.when
        : rule.when
          ? [rule.when]
          : [];
      if (
        matchers.length > 0 &&
        !matchers.some((matcher) => matchesPattern(promptText, matcher))
      ) {
        continue;
      }
      const rawAnswer =
        typeof rule.answer === "function"
          ? await rule.answer({ prompt, history })
          : rule.answer;
      return resolvePromptAnswer(prompt, rawAnswer);
    }
    if (fallback) return fallback({ prompt, history });
    throw new Error(
      `No scripted prompt responder matched prompt ${prompt.id}: ${promptText}`,
    );
  };
  Object.defineProperty(responder, "__granularScriptedPromptRules", {
    enumerable: false,
    value: rules,
  });
  return responder;
}

function extractJsonObject(text: string): Record<string, unknown> | null {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start === -1 || end === -1 || end < start) return null;
  const candidate = text.slice(start, end + 1);
  try {
    return JSON.parse(candidate) as Record<string, unknown>;
  } catch {
    const fallback: Record<string, unknown> = {};
    for (const fieldName of ["action", "reply", "code"]) {
      const field = extractJsonStringField(candidate, fieldName);
      if (field?.complete) {
        fallback[fieldName] = field.value;
      }
    }
    return Object.keys(fallback).length > 0 ? fallback : null;
  }
}

function extractJsonStringField(
  source: string,
  fieldName: string,
): { value: string; complete: boolean } | null {
  const keyIndex = source.indexOf(JSON.stringify(fieldName));
  if (keyIndex === -1) return null;

  const colonIndex = source.indexOf(":", keyIndex + fieldName.length + 2);
  if (colonIndex === -1) return null;

  let cursor = colonIndex + 1;
  while (cursor < source.length && /\s/.test(source[cursor] || "")) cursor += 1;
  if (source[cursor] !== '"') return null;

  cursor += 1;
  let value = "";
  while (cursor < source.length) {
    const char = source[cursor];
    if (char === '"') return { value, complete: true };
    if (char !== "\\") {
      value += char;
      cursor += 1;
      continue;
    }

    if (cursor + 1 >= source.length) return { value, complete: false };
    const escaped = source[cursor + 1];
    if (escaped === "n") value += "\n";
    else if (escaped === "r") value += "\r";
    else if (escaped === "t") value += "\t";
    else if (escaped === "b") value += "\b";
    else if (escaped === "f") value += "\f";
    else if (escaped === '"' || escaped === "\\" || escaped === "/") {
      value += escaped;
    } else if (escaped === "u") {
      const hex = source.slice(cursor + 2, cursor + 6);
      if (hex.length < 4 || !/^[0-9a-fA-F]{4}$/.test(hex)) {
        return { value, complete: false };
      }
      value += String.fromCharCode(Number.parseInt(hex, 16));
      cursor += 6;
      continue;
    } else {
      value += escaped;
    }
    cursor += 2;
  }

  return { value, complete: false };
}

function modelOutputInstruction(): string {
  return [
    "Return only a JSON object with this shape:",
    '{ "action": "reply" | "job", "code": string, "reply": string }',
    'Use "action":"reply" only when a plain conversational answer is enough and no live session state should change.',
    'Do not use "action":"reply" to promise future tool work; if the user asks to check, find, look up, inspect, read, reopen, summarize, transform, update, post, send, approve, schedule, reschedule, calculate, or confirm around session state, session files, generated files, tools, or a domain action, use "action":"job".',
    'If the user asks to use an attached file, uploaded file, generated file, previous output file, or "the summary/workbook/file you just created", choose "action":"job" and read it through [Runtime Imports] instead of answering from memory.',
    'Do not use "action":"reply" to say a record is not grounded yet; if the request names or describes a domain record, use "action":"job" and ground it from session state, relationships, searches, or visible read-only actions first.',
    'Before claiming you lack access, inspect the visible action list. If a visible read-only search, lookup, list, guidance, note, policy, or knowledge action can satisfy a "check", "find", "look up", or "whether we have guidance" request, choose "action":"job" and call it.',
    "Generated code must not report no matches for the primary human-described anchor after a single zero-result list/find/page call. Before that primary no-match return, retry the primary anchor with fewer text constraints or a distinct fallback such as owner/container grounding, relationship traversal, exact-id/path lookup, or shorter target-local search.",
    "If the latest request names an importable entity type, generated code must ground that class first; a previously installed parent class or related action method is not a substitute. After zero results in one class, pivot to the latest named importable class before reporting no match.",
    "When the user says they have a document/work item/event but no matching record exists yet, generated code must check the visible class-level new-record/create/preparation surface before returning no-match; if required create inputs are still missing and no prepared action can collect them, ask only for those inputs.",
    'Use "action":"job" when the next step should run code or mutate workflow state.',
    'When action is "job", include runnable code in "code" and emit the "code" field before any non-empty "reply" field so generated code comments can stream as progress.',
    "Generated job code must use plain ASCII punctuation in string literals and comments. Do not use curly quotes, smart apostrophes, en dashes, em dashes, or other typographic punctuation in code.",
    "Generated code must not reference prompt-only symbols such as runtimeImports, savedData, sessionFileManifest, recentReferences, workflowContext, workflowState, or capabilities. Copy concrete paths/ids from the prompt into strings, fetch records with documented imports, or use documented runtime globals.",
    "Generated code must follow [Runtime Imports]: import module exports from their listed module, use listed globals directly without importing them, and do not leave undeclared identifiers in the job.",
    "Generated action calls must use the exact input property names from the visible action schema. Do not invent synonym keys for required inputs.",
    "If multiple possible targets or a needed human decision blocks a requested operation, import userInteraction from @granular/session and put the pause inside code with userInteraction.askChoice(...), userInteraction.askText(...), or userInteraction.askConfirmation(...); listing candidates or asking only in reply text and returning is incomplete, including when ambiguity is discovered after a query returns several records.",
    "When a lookup before a mutation returns multiple plausible target records, generated code must ask for a grounded choice; do not mutate results[0], the earliest sorted record, or any other default pick unless the user supplied a unique identifier, ordinal, or selector.",
    "A bare pronoun such as it, that, or that one is not a unique mutation target when recentReferences, savedData, or the prior visible answer contains multiple compatible records. Do not let one exact recentReference path override that multi-record ambiguity; generated code must ask for a grounded choice before mutating.",
    "If a follow-up names the same/previous record and also names a related target or evidence type in a condition, use the same/previous record only as the anchor; traverse to the named related type before deciding or mutating.",
    "For owner/container plus target requests, generated code must ground the owner/container first, then discover the target through relationships, relationship filters, or short target-local search; do not combine owner/container words with target words in one target-class query or require owner/container words to appear in target-local title/summary fields.",
    "For requested categorical states, generated code should use positive exact filters or explicit local checks; do not use substring negation of another state as a proxy for the requested state.",
    "For conditional mutations based on a related evidence record, generated code order must be: load the action target or anchor, traverse to the related evidence record, call any visible status/lookup action, then decide whether to mutate. Do not decide, mutate, return, or reject the condition from parent/action-target fields before that evidence step.",
    "When a condition names an anchored noun phrase whose final noun is an entity type, the final entity type is the evidence record to test; use the earlier words only to ground or traverse to that record.",
    "Do not treat prior read-only summaries, cached parent fields, action-target fields, or stored related-record fields as fresh evidence for a later conditional mutation when a related evidence object and visible lookup/status action can be reached.",
    "Generated code must await declared relationship getters before checking arrays, iterating, or reading related-record fields.",
    "Generated code must call only relationship getters declared on the current record's class; if the target is not direct, walk the declared intermediate getter first instead of inventing a convenience getter.",
    "Generated code must use declared relationship getters before reading fields from related records; relationship filter fields are not guaranteed to be hydrated nested objects.",
    "For suitable/available candidate requests, generated code must call a visible availability, matching, or search action when one exists instead of relying only on current relationships or assignments.",
    "A follow-up to a refused bypass, export, external-send, or restricted-data request must keep the refusal boundary for the same referent; choose a reply refusal instead of running a mutation job.",
    "Visible answers for grounded named records should include the stored display name or identifier, not only the user's shorthand.",
    "When the user asks for specific fields, the reply must include every requested field or explicitly say which grounded field is unavailable after fetching the grounded record if saved state is partial.",
    "When matching action-returned candidates to grounded records, use the output schema's actual identifier fields, including id, path, or fields ending in Id; do not assume returned candidates have _graphPath.",
    "When resolving a choice answer, accept an unambiguous prefix or substring of an option label; do not fail just because the returned label is abbreviated.",
    "Do not discard availability/search results solely because a candidate is already assigned or related, unless the user asked for a different candidate.",
    "After verifying a user-authorized conditional mutation, call the action directly; do not add userInteraction.askConfirmation(...) solely because the mutation is visible to other people, customer-facing, or consequential. Confirm only when the user, policy, action metadata, or unresolved material uncertainty requires it.",
    "When a job identifies a specific record in its visible answer, display it with showObjects(...) from @granular/agent when the user should see or open it; otherwise save it with groundedObjects.save(...) from @granular/session only when it is needed for follow-up resolution.",
    "groundedObjects.save(...) accepts scalars, runtime records, sandbox instances, or arrays of those values; do not save plain action/effect result objects. Fetch a created record by its returned id/path before saving or displaying it.",
    "For requested record fields, read the documented properties from the fetched record before saying a value is unavailable.",
    'When action is "reply", include the user-facing answer in "reply".',
  ].join("\n");
}

export function createOpenAIChatTurnGenerator(options: {
  apiKey: string;
  model?: string;
  baseUrl?: string;
  temperature?: number;
  reasoningEffort?: "low" | "medium" | "high";
  headers?: Record<string, string>;
  usageContext?: GranularSpendContext;
  onUsage?: (usage: AgentEvalOpenAIUsageEvent) => void | Promise<void>;
}): AgentEvalTurnGenerator {
  const baseUrl = (options.baseUrl || "https://api.openai.com/v1").replace(
    /\/$/,
    "",
  );
  const model = options.model || "gpt-5.6-luna";
  const client = new OpenAI({
    apiKey: options.apiKey,
    baseURL: baseUrl,
    defaultHeaders: options.headers,
  });

  const emitUsage = async (
    rawUsage: unknown,
    requestId?: string | null,
    usageContext?: GranularSpendContext,
  ): Promise<void> => {
    if (!rawUsage || !options.onUsage) return;
    const spend = calculateOpenAITokenSpend(model, rawUsage);
    if (!spend) return;
    const mergedUsageContext = {
      ...(options.usageContext || {}),
      ...(usageContext || {}),
    };
    await options.onUsage({
      ...spend,
      source: "openai",
      lineItemType: "llm_tokens",
      operation: "chat.completions",
      requestId: requestId || null,
      usageContext: mergedUsageContext,
      rawUsage,
    });
  };

  return async (input) => {
    const messages: ChatCompletionMessageParam[] = [
      {
        role: "system",
        content: `${input.systemPrompt}\n\n${modelOutputInstruction()}`,
      },
      ...input.history.map(
        (message): ChatCompletionMessageParam => ({
          role: message.role,
          content: message.content,
        }),
      ),
      { role: "user", content: input.request },
    ];

    const payload: ChatCompletionCreateParamsNonStreaming = {
      model,
      messages,
      response_format: { type: "json_object" },
    };
    if (typeof options.temperature === "number") {
      payload.temperature = options.temperature;
    }
    if (options.reasoningEffort) {
      payload.reasoning_effort = options.reasoningEffort;
    }

    let lastError: Error | null = null;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        let raw: unknown;
        let text = "";
        let usage: unknown = null;
        let requestId: string | null = null;

        if (input.onTextDelta || input.onReplyDelta || input.onCodeDelta) {
          const streamPayload: ChatCompletionCreateParamsStreaming = {
            ...payload,
            stream: true,
            stream_options: { include_usage: true },
          };
          const stream = await client.chat.completions.create(streamPayload);
          let streamedReply = "";
          let streamedCode = "";
          const emitReplyDelta = async () => {
            if (!input.onReplyDelta) return;
            const replyField = extractJsonStringField(text, "reply");
            if (!replyField) return;
            const nextReply = replyField.value;
            if (!nextReply.startsWith(streamedReply)) return;
            const delta = nextReply.slice(streamedReply.length);
            if (!delta) return;
            streamedReply = nextReply;
            await input.onReplyDelta(delta);
          };
          const emitCodeDelta = async () => {
            if (!input.onCodeDelta) return;
            const codeField = extractJsonStringField(text, "code");
            if (!codeField) return;
            const nextCode = codeField.value;
            if (!nextCode.startsWith(streamedCode)) return;
            const delta = nextCode.slice(streamedCode.length);
            if (!delta) return;
            streamedCode = nextCode;
            await input.onCodeDelta(delta);
          };

          for await (const event of stream) {
            const eventRecord = event as unknown as Record<string, unknown>;
            requestId =
              requestId ||
              event.id ||
              (typeof eventRecord._request_id === "string"
                ? eventRecord._request_id
                : null);
            usage = event.usage || usage;
            const delta: unknown = event.choices?.[0]?.delta?.content;
            const deltaText =
              typeof delta === "string"
                ? delta
                : Array.isArray(delta)
                  ? delta
                      .map((part: unknown) => asRecord(part)?.text || "")
                      .join("")
                  : "";
            if (!deltaText) continue;
            text += deltaText;
            await input.onTextDelta?.(deltaText);
            await emitReplyDelta();
            await emitCodeDelta();
          }
          await emitReplyDelta();
          await emitCodeDelta();

          raw = { streamed: true, model, usage, request_id: requestId };
        } else {
          const completion: ChatCompletion =
            await client.chat.completions.create(payload);
          const completionRecord = completion as unknown as Record<
            string,
            unknown
          >;
          raw = completion;
          usage = completion.usage;
          requestId =
            (typeof completion.id === "string" ? completion.id : null) ||
            (typeof completionRecord._request_id === "string"
              ? completionRecord._request_id
              : null);
          const content = asRecord(
            asRecord(completion.choices[0])?.message,
          )?.content;
          text =
            typeof content === "string"
              ? content
              : Array.isArray(content)
                ? content.map((part) => asRecord(part)?.text || "").join("")
                : "";
        }

        await emitUsage(usage, requestId, input.usageContext);
        const parsed = extractJsonObject(text);
        if (!parsed) {
          if (attempt < 3) {
            await sleep(300 * attempt);
            continue;
          }
          return {
            reply: text.trim(),
            code: undefined,
            raw,
          };
        }

        return {
          reply: typeof parsed.reply === "string" ? parsed.reply : undefined,
          code: typeof parsed.code === "string" ? parsed.code : undefined,
          raw,
        };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        const status = Number((error as { status?: unknown })?.status);
        if (
          attempt < 3 &&
          ((Number.isFinite(status) && (status >= 500 || status === 429)) ||
            /socket connection was closed unexpectedly|ECONNRESET|network|timed out/i.test(
              lastError.message,
            ))
        ) {
          await sleep(500 * attempt);
          continue;
        }
        throw lastError;
      }
    }

    throw lastError || new Error("OpenAI chat generation failed");
  };
}

async function ensureDir(dir: string): Promise<void> {
  await mkdir(dir, { recursive: true });
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(`${label} timed out after ${ms}ms`)),
          ms,
        );
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

function getActionSummary(
  liveDoc: Record<string, unknown> | null,
  jobId: string,
): string[] {
  const jobsById = asRecord(asRecord(liveDoc?.jobs)?.byId) || {};
  const job = asRecord(jobsById[jobId]);
  const summary = Array.isArray(job?.actionSummary)
    ? job.actionSummary.filter(
        (line): line is string => typeof line === "string",
      )
    : [];
  const trace = Array.isArray(job?.actionTrace)
    ? job.actionTrace
        .map((event) => asRecord(event))
        .filter((event): event is Record<string, unknown> => Boolean(event))
    : [];
  const traceLines = trace
    .map((event) => {
      const kind = typeof event.kind === "string" ? event.kind : "";
      const action = typeof event.action === "string" ? event.action : "";
      const target = typeof event.target === "string" ? event.target : "";
      if (!action) return "";
      if (kind === "effect_call") {
        return `- Called ${target || action}`;
      }
      if (kind === "loop_op" && action === "ask_user") {
        return "- Asked the user for input";
      }
      if (kind === "loop_op" && action === "confirm") {
        return "- Requested confirmation";
      }
      return "";
    })
    .filter((line): line is string => Boolean(line));
  return Array.from(new Set([...summary, ...traceLines]));
}

function normalizeHeapSnapshot(
  heap: Record<string, unknown> | null | undefined,
): SessionHeapSnapshot {
  return {
    entriesByPath: (asRecord(heap?.entriesByPath) || {}) as unknown as Record<
      string,
      SessionHeapEntry
    >,
    listsByName: (asRecord(heap?.listsByName) || {}) as unknown as Record<
      string,
      SessionHeapList
    >,
    variablesByName: (asRecord(heap?.variablesByName) ||
      {}) as unknown as Record<string, SessionHeapVariable>,
    updatedAt:
      typeof heap?.updatedAt === "number" ? heap.updatedAt : Date.now(),
  };
}

function targetFromUiContext(
  context: Record<string, unknown> | null | undefined,
): { className: string; id: string; label?: string } | null {
  const target =
    asRecord(context?.target) ||
    asRecord(context?.currentPageObject) ||
    asRecord(context?.commentaryTarget);
  const className =
    typeof target?.className === "string" ? target.className : "";
  const id = typeof target?.id === "string" ? target.id : "";
  if (!className || !id) return null;
  return {
    className,
    id,
    label: typeof target?.label === "string" ? target.label : undefined,
  };
}

function heapEntryMatchesTarget(
  entry: SessionHeapEntry,
  target: { className: string; id: string },
): boolean {
  if (entry.className !== target.className) return false;
  if (entry.id === target.id) return true;
  const fields = asRecord(entry.fields);
  return fields?.real_id === target.id || fields?._realId === target.id;
}

function focusedHeapEntryPathsFromUiContext(
  heap: SessionHeapSnapshot,
  context: Record<string, unknown> | null | undefined,
): string[] {
  const target = targetFromUiContext(context);
  if (!target) return [];
  return Object.entries(heap.entriesByPath)
    .filter(([, entry]) => heapEntryMatchesTarget(entry, target))
    .map(([path]) => path);
}

function hasSessionDocumentContext(document: unknown): boolean {
  const doc = asRecord(document);
  if (!doc) return false;
  const heap = normalizeHeapSnapshot(asRecord(doc.heap));
  if (
    Object.keys(heap.entriesByPath).length > 0 ||
    Object.keys(heap.listsByName).length > 0 ||
    Object.keys(heap.variablesByName).length > 0
  ) {
    return true;
  }
  const domain = asRecord(doc.domain);
  const packages = asRecord(domain?.packages);
  if (packages && Object.keys(packages).length > 0) return true;
  const prompts = asRecord(doc.prompts);
  if (prompts && Object.keys(prompts).length > 0) return true;
  const workflows = asRecord(doc.workflows);
  if (workflows && Object.keys(workflows).length > 0) return true;
  return false;
}

async function waitForSessionDocumentContext(
  environment: EnvironmentSession,
  timeoutMs = 3_000,
): Promise<void> {
  if (hasSessionDocumentContext(environment.document)) return;

  await new Promise<void>((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    const settle = () => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(settle, timeoutMs);
    unsubscribe = environment.on("sync", (document) => {
      if (hasSessionDocumentContext(document)) {
        settle();
      }
    });
  });
}

function buildContinuationPreview(
  checkpoint: GranularAgentExecutionCheckpoint | null,
  noProgressCount: number,
): string {
  const lines = [
    `Controller no-progress count: ${noProgressCount}`,
    checkpoint?.latestJobStatus
      ? `Latest job status: ${checkpoint.latestJobStatus}`
      : null,
    checkpoint?.latestJobError
      ? `Latest job error: ${checkpoint.latestJobError}`
      : null,
    checkpoint?.latestActionSummary?.length
      ? `Latest action summary:\n${checkpoint.latestActionSummary.map((line) => `- ${line}`).join("\n")}`
      : null,
    checkpoint?.latestJobResult
      ? `Latest job result:\n${checkpoint.latestJobResult}`
      : null,
  ];
  return lines.filter(Boolean).join("\n\n");
}

function readableFeedItem(item: FeedItem): string {
  if (item.kind === "message" || item.kind === "feedback") {
    return item.payload.text;
  }
  if (item.kind === "objects") {
    return item.payload.label || "Displayed grounded objects";
  }
  if (item.kind === "table") {
    return item.payload.label || "Displayed a table";
  }
  if (item.kind === "artifact") {
    return item.payload.fallback.label || "Displayed an artifact";
  }
  if (item.kind === "file") {
    return item.payload.fallback.filename || "Displayed a file";
  }
  if (item.kind === "action_suggestion") {
    return item.payload.label;
  }
  if (item.kind === "prompt") {
    return item.payload.message || item.payload.title || "Requested input";
  }
  return "Feed item";
}

async function waitForJobOutcome(input: {
  environment: EnvironmentSession;
  job: {
    id: string;
    result: Promise<unknown>;
    on: (event: string, handler: (data: unknown) => void) => void;
  };
  boundaryTimestamp: number;
  timeoutMs: number;
  pollIntervalMs: number;
  onProgress?: (event: AgentEvalProgressEvent) => void;
  progressContext?: Pick<
    AgentEvalProgressEvent,
    "scenarioId" | "stepId" | "iteration" | "templateId" | "templateVersion"
  >;
}): Promise<
  | {
      kind: "completed";
      result: unknown;
      liveDoc: Record<string, unknown> | null;
      stdout: string[];
      stderr: string[];
    }
  | {
      kind: "prompt";
      prompts: Prompt[];
      liveDoc: Record<string, unknown> | null;
      stdout: string[];
      stderr: string[];
    }
> {
  const stdout: string[] = [];
  const stderr: string[] = [];
  let lastLiveDoc: Record<string, unknown> | null = null;
  let lastPromptCount = 0;
  let lastFeedItemCount = 0;
  let lastJobSummary: unknown = null;
  let lastActionSummaryKey = "";
  let lastActionSummaryLength = 0;
  let lastFeedItemKey = "";
  let lastFeedSequence = 0;
  let lastJobStatus = "";
  input.job.on("stdout", (line) => stdout.push(String(line)));
  input.job.on("stderr", (line) => stderr.push(String(line)));

  const startedAt = Date.now();
  while (Date.now() - startedAt < input.timeoutMs) {
    const liveDoc = cloneJson(input.environment.document) as Record<
      string,
      unknown
    >;
    lastLiveDoc = liveDoc;
    const prompts = filterPromptsByBoundary(
      liveDoc,
      getOpenPromptsFromDoc(liveDoc),
      input.boundaryTimestamp,
    );
    lastPromptCount = prompts.length;
    const feedItems = getJobFeedItems(liveDoc, input.job.id);
    lastFeedItemCount = feedItems.length;
    lastJobSummary =
      asRecord(asRecord(liveDoc.jobs)?.byId)?.[input.job.id] || null;
    const jobRecord = asRecord(lastJobSummary);
    const jobStatus =
      typeof jobRecord?.status === "string" ? jobRecord.status : "";
    if (jobStatus && jobStatus !== lastJobStatus) {
      lastJobStatus = jobStatus;
      input.onProgress?.({
        id: `${input.job.id}:status:${jobStatus}:${Date.now()}`,
        timestamp: new Date().toISOString(),
        phase: "job",
        status: "running",
        jobId: input.job.id,
        title: `Job ${jobStatus}`,
        message: `Granular job ${input.job.id}`,
        data: jobRecord,
        ...input.progressContext,
      });
    }
    const actionSummary = getActionSummary(liveDoc, input.job.id);
    const actionSummaryKey = JSON.stringify(actionSummary);
    if (actionSummary.length && actionSummaryKey !== lastActionSummaryKey) {
      const newActions = actionSummary.slice(lastActionSummaryLength);
      lastActionSummaryLength = actionSummary.length;
      lastActionSummaryKey = actionSummaryKey;
      input.onProgress?.({
        id: `${input.job.id}:actions:${Date.now()}`,
        timestamp: new Date().toISOString(),
        phase: "job",
        status: "running",
        jobId: input.job.id,
        title: newActions.length === 1 ? "Action observed" : "Actions observed",
        message: (newActions.length ? newActions : actionSummary).join("\n"),
        data: { actionSummary, newActions },
        ...input.progressContext,
      });
    }
    const feedItemKey = feedItems
      .map((item) => `${item.sequence}:${item.id}`)
      .join("|");
    if (feedItems.length && feedItemKey !== lastFeedItemKey) {
      const newItems = feedItems.filter(
        (item) => item.sequence > lastFeedSequence,
      );
      lastFeedItemKey = feedItemKey;
      const latestItem = newItems.at(-1);
      if (latestItem) {
        lastFeedSequence = latestItem.sequence;
        input.onProgress?.({
          id: `${input.job.id}:feed:${latestItem.sequence}`,
          timestamp: new Date().toISOString(),
          phase: "job",
          status: "running",
          jobId: input.job.id,
          title: "Feed item",
          message: readableFeedItem(latestItem),
          data: { feedItems, newItems },
          ...input.progressContext,
        });
      }
    }
    if (prompts.length > 0) {
      input.onProgress?.({
        id: `${input.job.id}:prompt:${prompts[0]?.id || Date.now()}`,
        timestamp: new Date().toISOString(),
        phase: "prompt",
        status: "running",
        jobId: input.job.id,
        title: "Waiting for prompt answer",
        message: prompts[0]?.message || prompts[0]?.title,
        data: { prompts },
        ...input.progressContext,
      });
      return { kind: "prompt", prompts, liveDoc, stdout, stderr };
    }

    try {
      const result = await withTimeout(
        input.job.result,
        input.pollIntervalMs,
        `job ${input.job.id} tick`,
      );
      input.onProgress?.({
        id: `${input.job.id}:completed:${Date.now()}`,
        timestamp: new Date().toISOString(),
        phase: "job",
        status: "passed",
        jobId: input.job.id,
        title: "Job completed",
        message: JSON.stringify(result)?.slice(0, 800),
        data: { result, stdout, stderr },
        ...input.progressContext,
      });
      return { kind: "completed", result, liveDoc, stdout, stderr };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (!/timed out after/.test(message)) throw error;
    }
  }

  const diagnostics = {
    elapsedMs: Date.now() - startedAt,
    promptCount: lastPromptCount,
    feedItemCount: lastFeedItemCount,
    stdoutTail: stdout.slice(-5),
    stderrTail: stderr.slice(-5),
    job: lastJobSummary,
    hasLiveDoc: Boolean(lastLiveDoc),
  };
  throw new Error(
    `Job ${input.job.id} timed out after ${input.timeoutMs}ms. Diagnostics: ${JSON.stringify(diagnostics)}`,
  );
}

export async function generateTurnWithRepair(
  generator: AgentEvalTurnGenerator,
  input: AgentEvalGenerationInput,
): Promise<AgentEvalGenerationOutput> {
  const generationAttempts: AgentEvalGenerationAttempt[] = [];
  let request = input.request;
  let repairIssues = input.repairIssues || [];

  for (let attempt = input.attempt; attempt < input.attempt + 3; attempt += 1) {
    const output = await generator({
      ...input,
      attempt,
      request,
      repairIssues,
    });
    const issues = output.code ? reviewGeneratedJobCode(output.code) : [];
    generationAttempts.push({
      attempt,
      request,
      repairIssues,
      reply: output.reply,
      code: output.code,
      raw: output.raw,
    });

    if (!output.code || issues.length === 0) {
      return { ...output, generationAttempts };
    }

    repairIssues = issues;
    request = [
      input.request,
      "",
      "The previous generated job code failed preflight review against [Runtime Imports] and the runtime contract.",
      "Return a corrected JSON object. Keep the user's requested behavior, but fix every issue below before execution.",
      "If an issue says workflow path, blockers, permissions, or readiness were derived from raw status fields, remove the status/current-state ladder. Pick the declared state handle that matches the user's requested outcome, call plan()/blockers()/permissions() first, then open/show that handle or explain its structured blockers.",
      "If an issue says an unsafe type assertion was used, remove the cast and use declared fields or typed helpers directly. For state plans, use plan.blockers, plan.permissions, plan.steps, plan.summary, or blocker-formatting helpers without hand-written object casts.",
      "If the previous code stopped after no matching record for a user-supplied document/work item, preserve the lookup but check declared new-record/create/preparation surfaces next. Import the backend action module if needed, or ask only for missing required create inputs when no prepared action can collect them.",
      "",
      "Preflight issues:",
      ...issues.map((issue) => `- ${issue.code}: ${issue.message}`),
      "",
      "Previous code:",
      "```ts",
      output.code,
      "```",
    ].join("\n");
  }

  const unresolvedIssues = repairIssues
    .map((issue) => `${issue.code}: ${issue.message}`)
    .join("\n");
  throw new Error(
    `Generated job failed preflight review after ${generationAttempts.length} attempt(s):\n${unresolvedIssues}`,
  );
}

async function writeJson(filePath: string, value: unknown): Promise<void> {
  await writeFile(filePath, `${JSON.stringify(value, null, 2)}\n`);
}

function safePathSegment(value: string | undefined, fallback: string): string {
  const normalized = (value || "").trim().toLowerCase();
  const sanitized = normalized
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 128);
  return sanitized || fallback;
}

function extractToolCalls(rawGeneration: unknown): unknown[] | null {
  const raw = asRecord(rawGeneration);
  if (!raw) return null;
  const choices = asArray(raw.choices);
  const firstChoiceMessage = asRecord(choices[0]);
  const message = asRecord(firstChoiceMessage?.message);
  const toolCalls = asArray(message?.tool_calls);
  if (toolCalls.length > 0) return toolCalls;
  return asArray(raw.tool_calls).length > 0 ? asArray(raw.tool_calls) : null;
}

function buildTurnMdxReport(input: {
  status: "completed" | "pending" | "failed";
  conversation: AgentEvalConversation;
  request: string;
  requestTimestamp: number;
  turnLog: AgentEvalSessionLogTurn;
  responseText?: string;
  terminalKind?: "reply" | "closure";
  actionSummary?: string[];
  promptInteractions?: AgentEvalPromptInteraction[];
  result?: unknown;
  prompts?: Prompt[];
  verification?: unknown;
  error?: string;
}): string {
  const {
    status,
    conversation,
    request,
    requestTimestamp,
    turnLog,
    responseText,
    terminalKind,
    actionSummary = [],
    promptInteractions = [],
    result,
    prompts = [],
    verification,
    error,
  } = input;
  const iterationLines: string[] = [];

  for (const iteration of turnLog.iterations) {
    iterationLines.push(`### Iteration ${iteration.iteration}`);
    iterationLines.push(
      `- Request: ${iteration.request}`,
      `- Generation duration: ${
        iteration.generationDurationMs !== undefined
          ? `${iteration.generationDurationMs}ms`
          : "unknown"
      }`,
    );
    if (iteration.templateId) {
      iterationLines.push(
        `- Template: ${iteration.templateId}@${iteration.templateVersion || "unknown"}`,
      );
    }
    if (iteration.templateHash) {
      iterationLines.push(`- Template hash: ${iteration.templateHash}`);
    }
    if (iteration.promptInstanceHash) {
      iterationLines.push(`- Prompt hash: ${iteration.promptInstanceHash}`);
    }
    iterationLines.push("", "#### Full prompt sent to LLM", "");
    iterationLines.push(fenced(iteration.systemPrompt, "text"));
    if (iteration.generationReply?.trim()) {
      iterationLines.push(
        "",
        "#### LLM reply text",
        iteration.generationReply.trim(),
        "",
      );
    }
    if (iteration.generatedCode?.trim()) {
      iterationLines.push(
        "#### Generated code",
        "",
        fenced(iteration.generatedCode.trim(), "ts"),
      );
    }

    const toolCalls = extractToolCalls(iteration.rawGeneration);
    iterationLines.push("#### Tool calls / raw generation", "");
    if (toolCalls) {
      iterationLines.push(fenced(JSON.stringify(toolCalls, null, 2), "json"));
    } else if (iteration.rawGeneration) {
      iterationLines.push(
        fenced(JSON.stringify(iteration.rawGeneration, null, 2), "json"),
      );
    } else {
      iterationLines.push("_No tool call information._");
    }

    if (iteration.tokenUsage) {
      iterationLines.push(
        "",
        "#### Token usage",
        ...formatTokenUsage(iteration.tokenUsage),
      );
    }
    if (iteration.responseText?.trim()) {
      iterationLines.push(
        "",
        "#### Runtime/prompt outcome",
        iteration.responseText,
      );
    }
    if (iteration.actionSummary?.length) {
      iterationLines.push("", "#### Action summary", "");
      iterationLines.push(
        ...iteration.actionSummary.map((line) => `- ${line}`),
      );
    }
    if (iteration.continuation) {
      iterationLines.push(
        "",
        "#### Continuation",
        "",
        jsonBlock(iteration.continuation),
      );
    }
    if (iteration.result !== undefined) {
      iterationLines.push("", "#### Result", "", jsonBlock(iteration.result));
    }
    if (iteration.error) {
      iterationLines.push("", `#### Error`, "", iteration.error);
    }
    iterationLines.push("", "---", "");
  }

  const promptEventLines = conversation.promptEvents
    .filter((event) => event.receivedAt >= requestTimestamp)
    .map((event, index) => {
      const prompt = event.prompt;
      return `${index + 1}. ${prompt.type} ${prompt.title || ""} ${
        prompt.message ? `— ${prompt.message}` : ""
      }`;
    });

  const historyLines = conversation.history.map((entry, index) => {
    const label = `${index + 1}. ${entry.role}`;
    const detail = entry.content || (entry.code ? "(code)" : "");
    return `${label}: ${detail || "(no text)"}`;
  });

  const lines = [
    "# Local Agent Query Report",
    "",
    `- timestamp: ${new Date(requestTimestamp).toISOString()}`,
    `- turn: ${turnLog.turnNumber} (${turnLog.turnId})`,
    `- status: ${status}`,
    "",
    "## Table of contents",
    "- [Metadata](#metadata)",
    "- [User query and context](#user-query-and-context)",
    "- [Conversation history](#conversation-history)",
    "- [Harness loop iterations](#harness-loop-iterations)",
    "- [Result and interactions](#result-and-interactions)",
    "",
    "## Metadata",
    `- Ontology: ${conversation.environment.ontologyId}`,
    `- Subject: ${conversation.environment.subjectId}`,
    `- Session: ${conversation.environment.sessionId}`,
    `- Environment: ${conversation.environment.environmentId}`,
    `- Sandbox: ${conversation.environment.sandboxId}`,
    `- Permission profile: ${conversation.environment.permissionProfileId}`,
    `- Conversation artifacts: ${turnLog.turnDir}`,
    "",
    "## User query and context",
    "",
    "### Request",
    fenced(request, "text"),
    "",
    "### Open prompts at/after request time",
  ];

  if (promptEventLines.length) {
    lines.push(...promptEventLines.map((line) => `- ${line}`));
  } else {
    lines.push("- _None");
  }

  lines.push(
    "",
    "## Conversation history",
    ...historyLines.map((line) => `- ${line}`),
    "",
    "## Harness loop iterations",
    "",
    ...iterationLines,
    "## Result and interactions",
    "",
  );

  if (responseText) {
    lines.push("### Final response", responseText, "");
  }
  if (terminalKind) {
    lines.push(`### Terminal kind`, terminalKind, "");
  }
  lines.push("### Action summary");
  if (actionSummary.length) {
    lines.push(...actionSummary.map((line) => `- ${line}`));
  } else {
    lines.push("- None");
  }
  lines.push("", "### User interactions");
  if (promptInteractions.length) {
    for (const interaction of promptInteractions) {
      lines.push(
        `- ${interaction.type} ${interaction.message || interaction.title} -> ${JSON.stringify(interaction.answer)}`,
      );
    }
  } else {
    lines.push("- None");
  }

  lines.push("", "### Pending prompts");
  if (prompts.length) {
    for (const prompt of prompts) {
      lines.push(
        `- ${prompt.type} ${prompt.title || ""} ${prompt.message || ""}`,
      );
    }
  } else {
    lines.push("- None");
  }
  if (result !== undefined) {
    lines.push("", "### Result payload", "", jsonBlock(result));
  }
  if (verification !== undefined) {
    lines.push("", "### Verification", "", jsonBlock(verification));
  }
  if (error) {
    lines.push("", `### Error`, "", error);
  }
  if (turnLog.error) {
    lines.push("", "### Turn log error", "", turnLog.error);
  }
  lines.push("");
  if (iterationLines.length === 0) {
    lines.splice(
      lines.indexOf("## Harness loop iterations") + 1,
      0,
      "- _No iterations recorded._",
    );
  }
  return `${lines.join("\n")}\n`;
}

function describeScenarioBehavior(result: AgentEvalResult): string {
  if (result.scenario.description?.trim()) {
    return result.scenario.description.trim();
  }
  const steps = result.steps?.length
    ? result.steps
    : buildScenarioSteps(result.scenario).map((step, index) => ({
        id: step.id || `step-${index + 1}`,
        request: step.request,
      }));
  const stepSummary = steps
    .map((step, index) => {
      const request = step.request.replace(/\s+/g, " ").trim();
      return `${index + 1}. ${step.id}: ${request}`;
    })
    .join(" ");
  return [
    `This report tests scenario \`${result.scenario.id}\` across ${steps.length} user turn${steps.length === 1 ? "" : "s"}.`,
    "It verifies that the agent grounds the natural-language request in the current ontology/session context, generates the expected job or refusal, and that the runtime executes or blocks the resulting behavior correctly.",
    stepSummary,
  ]
    .filter(Boolean)
    .join(" ");
}

function describeJobSource(result: AgentEvalResult): string {
  if (result.scenario.jobSource === "hardcoded") {
    return "Hardcoded deterministic job code supplied by the test.";
  }
  if (result.scenario.jobSource === "mixed") {
    return "Mixed: LLM-generated agent jobs plus hardcoded setup/inspection jobs supplied by the test.";
  }
  const hasGeneratedCode = Boolean(
    result.finalCode || result.steps?.some((step) => step.finalCode),
  );
  if (hasGeneratedCode) {
    return "LLM-generated agent job code. Setup, direct inspections, and assertions are hardcoded by the test harness.";
  }
  return "LLM-generated agent response. Setup, direct inspections, and assertions are hardcoded by the test harness.";
}

function buildResultReport(result: AgentEvalResult): string {
  const stepSection = result.steps?.length
    ? [
        "## Steps",
        ...result.steps.flatMap((step, index) => [
          `### ${index + 1}. ${step.id}`,
          `- Request: ${step.request}`,
          `- Reply: ${step.responseText || "_No reply_"}`,
          `- Terminal kind: ${step.terminalKind}`,
          `- Actions: ${step.actionSummary.length ? step.actionSummary.join(" | ") : "None"}`,
          `- Prompt interactions: ${step.promptInteractions.length ? step.promptInteractions.map((interaction) => `[${interaction.type}] ${interaction.message || interaction.title} -> ${JSON.stringify(interaction.answer)}`).join(" | ") : "None"}`,
          "",
        ]),
      ]
    : [];

  const lines = [
    `# Scenario Report: ${result.scenario.id}`,
    "",
    "## Behavior Under Test",
    describeScenarioBehavior(result),
    "",
    "## Job Source",
    describeJobSource(result),
    "",
    "## Token Usage And Cost",
    ...formatTokenUsage(result.tokenUsage),
    "",
    "## Request",
    result.scenario.request ||
      result.steps?.[0]?.request ||
      "_No single request_",
    "",
    "## Outcome",
    `- Status: ${result.status}`,
    `- Terminal kind: ${result.terminalKind}`,
    `- Reply: ${result.responseText || "_No reply_"}`,
    "",
    "## Actions",
    ...(result.actionSummary.length
      ? result.actionSummary.map((line) => `- ${line}`)
      : ["- None"]),
    "",
    "## Prompt Interactions",
    ...(result.promptInteractions.length
      ? result.promptInteractions.map(
          (interaction) =>
            `- [${interaction.type}] ${interaction.message || interaction.title} -> ${JSON.stringify(interaction.answer)}`,
        )
      : ["- None"]),
    "",
    ...stepSection,
    "## Raw Files",
    `- [result.json](./result.json)`,
    `- [report.json](./report.json)`,
    ...(result.verification !== null
      ? [`- [verification.json](./verification.json)`]
      : []),
  ];
  return `${lines.join("\n")}\n`;
}

function buildSuiteIndex(results: AgentEvalResult[]): string {
  const totalUsage = aggregateTokenUsage(
    results.map((result) => result.tokenUsage),
  );
  const lines = [
    "# Agent Eval Report Index",
    "",
    "## Token Usage And Cost",
    ...formatTokenUsage(totalUsage),
    "",
    "## Session Logs",
    "",
    "- [Readable chronological logs](./logs/README.md)",
    "",
    "## Scenario Reports",
    "",
    ...results.map(
      (result) =>
        `- [${result.scenario.id}](./${result.scenario.id}/REPORT.md) - ${result.status}`,
    ),
  ];
  return `${lines.join("\n")}\n`;
}

function buildLogsIndex(results: AgentEvalResult[]): string {
  const totalUsage = aggregateTokenUsage(
    results.map((result) => result.tokenUsage),
  );
  const lines = [
    "# Agent Eval Session Logs",
    "",
    "Each file is a chronological session report with user requests, agent responses, generated code, runtime actions/results, and system prompts at the end.",
    "",
    "## Token Usage And Cost",
    ...formatTokenUsage(totalUsage),
    "",
    ...results.map((result) => {
      const logName = `${slugify(result.scenario.id)}.md`;
      return `- [${result.scenario.id}](./${logName}) - ${result.status} - ${formatUsd(result.tokenUsage?.totalCostUsd || 0)}`;
    }),
  ];
  return `${lines.join("\n")}\n`;
}

function fenced(value: string, language = ""): string {
  const fence = value.includes("```") ? "````" : "```";
  return `${fence}${language}\n${value}\n${fence}`;
}

function jsonBlock(value: unknown): string {
  return fenced(JSON.stringify(value, null, 2), "json");
}

function buildSessionLogReport(input: {
  conversation: AgentEvalConversation;
  result?: AgentEvalResult;
  error?: string;
}): string {
  const { conversation, result, error } = input;
  const logTurns = conversation.logTurns || [];
  const systemPrompts = logTurns.flatMap((turn) =>
    turn.iterations.map((iteration) => ({
      turn,
      iteration,
    })),
  );
  const lines: string[] = [
    `# Session Log: ${conversation.label}`,
    "",
    "## Behavior Under Test",
    result
      ? describeScenarioBehavior(result)
      : "This session log captures the chronological agent/runtime behavior for a scenario that did not complete a structured result.",
    "",
    "## Job Source",
    result
      ? describeJobSource(result)
      : "LLM-generated agent jobs when generation completed; setup and harness assertions are hardcoded by the test harness.",
    "",
    "## Token Usage And Cost",
    ...formatTokenUsage(
      result?.tokenUsage || aggregateConversationTokenUsage(conversation),
    ),
    "",
    "## Metadata",
    `- Session id: \`${conversation.environment.sessionId}\``,
    `- Environment id: \`${conversation.environment.environmentId}\``,
    `- Sandbox id: \`${conversation.environment.sandboxId}\``,
    `- Status: ${result?.status || (error ? "failed" : "unknown")}`,
    ...(systemPrompts[0]?.iteration.templateId
      ? [
          `- Harness template: \`${systemPrompts[0].iteration.templateId}@${systemPrompts[0].iteration.templateVersion || "unknown"}\``,
          `- Template hash: \`${systemPrompts[0].iteration.templateHash || "unknown"}\``,
        ]
      : []),
    ...(result?.error || error ? [`- Error: ${result?.error || error}`] : []),
    "",
    "## Conversation",
  ];

  for (const turn of logTurns) {
    lines.push("", `### Turn ${turn.turnNumber}: ${turn.turnId}`, "");
    lines.push("**User**", "");
    lines.push(turn.request, "");

    for (const iteration of turn.iterations) {
      lines.push(`#### Agent Generation ${iteration.iteration}`, "");
      lines.push(
        "**Token Usage And Cost**",
        "",
        ...formatTokenUsage(iteration.tokenUsage),
        "",
      );
      if ((iteration.generationAttempts?.length || 0) > 1) {
        lines.push("**Generation Attempts**", "");
        for (const attempt of iteration.generationAttempts || []) {
          lines.push(`Attempt ${attempt.attempt}`, "");
          if (attempt.repairIssues?.length) {
            lines.push("Repair issues:", "");
            for (const issue of attempt.repairIssues) {
              lines.push(`- ${issue.code}: ${issue.message}`);
            }
            lines.push("");
          }
          lines.push("Request", "", fenced(attempt.request, "text"), "");
          if (attempt.reply?.trim()) {
            lines.push("Draft reply", "", attempt.reply.trim(), "");
          }
          if (attempt.code?.trim()) {
            lines.push("Code", "", fenced(attempt.code.trim(), "ts"), "");
          }
        }
      }
      if (iteration.generationReply?.trim()) {
        lines.push("**Draft Reply**", "", iteration.generationReply.trim(), "");
      }
      if (iteration.generatedCode?.trim()) {
        lines.push(
          "**Generated Code**",
          "",
          fenced(iteration.generatedCode.trim(), "ts"),
          "",
        );
      } else {
        lines.push("**Generated Code**", "", "_No code generated._", "");
      }
      if (iteration.promptInteractions?.length) {
        lines.push("**Structured User Input**", "");
        for (const interaction of iteration.promptInteractions) {
          lines.push(
            `- ${interaction.type}: ${interaction.message || interaction.title} -> \`${JSON.stringify(interaction.answer)}\``,
          );
        }
        lines.push("");
      }
      if (iteration.actionSummary?.length) {
        lines.push("**Runtime Actions**", "");
        for (const action of iteration.actionSummary) lines.push(`- ${action}`);
        lines.push("");
      }
      if (iteration.responseText?.trim()) {
        lines.push("**Agent Response**", "", iteration.responseText.trim(), "");
      }
      if (iteration.continuation) {
        lines.push(
          "**Harness Continuation**",
          "",
          jsonBlock(iteration.continuation),
          "",
        );
      }
      if (iteration.result !== undefined) {
        lines.push("**Runtime Result**", "", jsonBlock(iteration.result), "");
      }
      if (iteration.error) {
        lines.push("**Error**", "", iteration.error, "");
      }
    }

    if (turn.completed) {
      lines.push(
        "**Turn Final Response**",
        "",
        turn.completed.responseText || "_No reply_",
        "",
      );
      if (turn.completed.actionSummary.length) {
        lines.push("**Turn Final Actions**", "");
        for (const action of turn.completed.actionSummary)
          lines.push(`- ${action}`);
        lines.push("");
      }
    }
    if (turn.error) {
      lines.push("**Turn Error**", "", turn.error, "");
    }
  }

  lines.push("", "## System Prompts", "");
  if (!systemPrompts.length) {
    lines.push("_No system prompts captured._", "");
  } else {
    for (const { turn, iteration } of systemPrompts) {
      lines.push(
        `### Turn ${turn.turnNumber}, Generation ${iteration.iteration}`,
        "",
        ...(iteration.templateId
          ? [
              `- Template: \`${iteration.templateId}@${iteration.templateVersion || "unknown"}\``,
              `- Template hash: \`${iteration.templateHash || "unknown"}\``,
              `- Prompt hash: \`${iteration.promptInstanceHash || "unknown"}\``,
              "",
            ]
          : []),
        fenced(iteration.systemPrompt, "text"),
        "",
      );
    }
  }

  return `${lines.join("\n")}\n`;
}

async function writeSessionLogReport(input: {
  artifactDir: string;
  conversation: AgentEvalConversation;
  result?: AgentEvalResult;
  error?: string;
}) {
  const logsDir = path.join(input.artifactDir, "logs");
  await ensureDir(logsDir);
  await writeFile(
    path.join(logsDir, `${slugify(input.conversation.label)}.md`),
    buildSessionLogReport(input),
  );
}

function findTurnLog(
  conversation: AgentEvalConversation,
  turnDir: string,
): AgentEvalSessionLogTurn | undefined {
  return conversation.logTurns.find((turn) => turn.turnDir === turnDir);
}

function latestIterationLog(
  turn: AgentEvalSessionLogTurn | undefined,
): AgentEvalSessionLogIteration | undefined {
  return turn?.iterations[turn.iterations.length - 1];
}

async function applySetup(
  setup: AgentEvalSetup | undefined,
  context: AgentEvalSetupContext & { granular: Granular },
): Promise<void> {
  if (!setup) return;
  if (setup.manifest) {
    await context.environment.applyManifest(setup.manifest);
  }
  if (setup.records?.length) {
    await context.environment.recordObjects(setup.records);
  }
  if (setup.effects?.length) {
    await context.granular
      .ontology(context.environment.sandboxId)
      .effects.registerMany(setup.effects);
  }
  if (setup.run) {
    await setup.run(context);
  }
}

function isTransientEvalError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /socket connection was closed unexpectedly/i.test(message) ||
    /timed out after/i.test(message) ||
    /ECONNRESET/i.test(message) ||
    /network/i.test(message) ||
    /429/.test(message)
  );
}

function assertionPassed(
  id: string,
  run: (assertionLabel: string) => void,
): AgentEvalAssertion {
  try {
    run(id);
    return { id, label: id, status: "passed" };
  } catch (error) {
    return {
      id,
      label: id,
      status: "failed",
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

function hasMatchers(matchers?: EvalMatcher[]): boolean {
  return Boolean(matchers?.length);
}

function evaluateExpectationAssertions(input: {
  scenarioId: string;
  stepIndex: number;
  expect?: AgentEvalExpectations;
  completed: AgentEvalCompletedTurn;
}): AgentEvalAssertion[] {
  const expect = input.expect;
  if (!expect) return [];
  const prefix = `${input.scenarioId} step ${input.stepIndex + 1}`;
  const assertions: AgentEvalAssertion[] = [];
  const actionText = input.completed.actionSummary.join("\n");
  const codeText = input.completed.finalCode || "";
  const displaySurface = [
    input.completed.responseText,
    actionText,
    codeText,
    JSON.stringify(input.completed.result),
  ].join("\n");
  const promptSurface = input.completed.promptInteractions
    .map((interaction) =>
      [interaction.type, interaction.title, interaction.message].join(" "),
    )
    .join("\n");

  if (hasMatchers(expect.replyIncludes) || hasMatchers(expect.replyExcludes)) {
    assertions.push(
      assertionPassed("reply", () =>
        assertMatches(
          `Reply for ${prefix}`,
          input.completed.responseText,
          expect.replyIncludes,
          expect.replyExcludes,
        ),
      ),
    );
  }
  if (
    hasMatchers(expect.actionIncludes) ||
    hasMatchers(expect.actionExcludes)
  ) {
    assertions.push(
      assertionPassed("actions", () =>
        assertMatches(
          `Action summary for ${prefix}`,
          actionText,
          expect.actionIncludes,
          expect.actionExcludes,
        ),
      ),
    );
  }
  if (hasMatchers(expect.codeIncludes) || hasMatchers(expect.codeExcludes)) {
    assertions.push(
      assertionPassed("code", () =>
        assertMatches(
          `Generated code for ${prefix}`,
          codeText,
          expect.codeIncludes,
          expect.codeExcludes,
        ),
      ),
    );
  }
  if (
    hasMatchers(expect.actions?.required) ||
    hasMatchers(expect.actions?.forbidden)
  ) {
    assertions.push(
      assertionPassed("required-actions", () =>
        assertMatches(
          `Required actions for ${prefix}`,
          actionText,
          expect.actions?.required,
          expect.actions?.forbidden,
        ),
      ),
    );
  }
  if (
    hasMatchers(expect.presentation?.mustMention) ||
    hasMatchers(expect.presentation?.mustNotMention)
  ) {
    assertions.push(
      assertionPassed("presentation", () =>
        assertMatches(
          `Presentation for ${prefix}`,
          input.completed.responseText,
          expect.presentation?.mustMention,
          expect.presentation?.mustNotMention,
        ),
      ),
    );
  }
  if (hasMatchers(expect.presentation?.mustDisplayOrSave)) {
    assertions.push(
      assertionPassed("display", () =>
        assertMatches(
          `Displayed or saved records for ${prefix}`,
          displaySurface,
          expect.presentation?.mustDisplayOrSave,
        ),
      ),
    );
  }
  if (expect.prompts?.requiredChoiceWhenAmbiguous) {
    assertions.push(
      assertionPassed("choice", () => {
        if (
          !input.completed.promptInteractions.some(
            (interaction) => interaction.type === "choice",
          )
        ) {
          throw new Error(`Expected ${prefix} to use a choice prompt.`);
        }
      }),
    );
  }
  for (const forbiddenType of expect.prompts?.forbiddenPromptTypes || []) {
    assertions.push(
      assertionPassed(`forbidden-prompt:${forbiddenType}`, () => {
        if (
          input.completed.promptInteractions.some(
            (interaction) => interaction.type === forbiddenType,
          )
        ) {
          throw new Error(
            `Prompt interactions for ${prefix} used forbidden prompt type ${forbiddenType}.`,
          );
        }
      }),
    );
  }
  if (expect.prompts?.requiredConfirmationBefore?.length) {
    assertions.push(
      assertionPassed("confirmation", () =>
        assertMatches(
          `Confirmation prompts for ${prefix}`,
          [actionText, promptSurface].join("\n"),
          expect.prompts?.requiredConfirmationBefore,
        ),
      ),
    );
  }
  return assertions;
}

export async function runAgentEvalSuite(options: {
  harness: ReturnType<typeof createAgentEvalHarness>;
  scenarios: AgentEvalScenario[];
}): Promise<AgentEvalSuiteResult> {
  const results: AgentEvalResult[] = [];
  for (const scenario of options.scenarios) {
    await options.harness.emitProgress?.({
      phase: "scenario",
      status: "running",
      scenarioId: scenario.id,
      title: "Scenario started",
      message: scenario.description || scenario.request || scenario.id,
      data: {
        scenarioId: scenario.id,
        behaviorBuckets: scenario.behaviorBuckets,
      },
    });
    let attempt = 0;
    let finalResult: AgentEvalResult | null = null;
    while (attempt < 2 && !finalResult) {
      attempt += 1;
      const conversation = await options.harness.openConversation(
        attempt === 1 ? scenario.id : `${scenario.id}-retry-${attempt}`,
      );
      try {
        await applySetup(scenario.setup, {
          granular: options.harness.granular,
          conversation,
          environment: conversation.environment,
          turnDir: conversation.artifactDir,
        });

        const stepResults: AgentEvalStepResult[] = [];
        const steps = buildScenarioSteps(scenario);

        for (let index = 0; index < steps.length; index += 1) {
          const step = steps[index]!;
          const completed = await options.harness.runTurn({
            conversation,
            request: step.request,
            prepare: async (ctx) => {
              await applySetup(step.setup, {
                granular: options.harness.granular,
                ...ctx,
              });
            },
            human: step.human,
            maxIterations: step.maxIterations,
            autoAnswerPrompts: step.autoAnswerPrompts,
          });
          if ("prompts" in completed) {
            throw new Error(
              `Scenario ${scenario.id} step ${index + 1} paused for human input instead of completing automatically`,
            );
          }

          const inspectionResults: unknown[] = [];
          const stepChecks = asArray(step.check);
          const stepInspections = asArray(step.inspect);
          const context: AgentEvalCheckContext = {
            conversation,
            environment: conversation.environment,
            turnDir: completed.turnDir,
            request: completed.request,
            responseText: completed.responseText,
            terminalKind: completed.terminalKind,
            finalCode: completed.finalCode,
            actionSummary: completed.actionSummary,
            promptInteractions: completed.promptInteractions,
            result: completed.result,
            heap: normalizeHeapSnapshot(
              asRecord(
                (
                  cloneJson(conversation.environment.document) as Record<
                    string,
                    unknown
                  >
                )?.heap,
              ),
            ),
            openPrompts: getOpenPromptsFromDoc(
              cloneJson(conversation.environment.document) as Record<
                string,
                unknown
              >,
            ),
            liveDoc: cloneJson(conversation.environment.document) as Record<
              string,
              unknown
            >,
            inspect: async (code: string) => {
              const session = conversation.environment;
              const job = await session.submitJob(code);
              return withTimeout(
                job.result,
                90_000,
                `inspection job ${job.id}`,
              );
            },
            assertMatches,
          };

          const assertions = evaluateExpectationAssertions({
            scenarioId: scenario.id,
            stepIndex: index,
            expect: step.expect,
            completed,
          });
          for (const assertion of assertions) {
            await options.harness.emitProgress?.({
              phase: "assertion",
              status: assertion.status,
              scenarioId: scenario.id,
              stepId: step.id || `step-${index + 1}`,
              title: assertion.label,
              message: assertion.message,
              data: assertion,
            });
          }
          const failedAssertion = assertions.find(
            (assertion) => assertion.status === "failed",
          );
          if (failedAssertion) {
            throw new Error(failedAssertion.message || failedAssertion.label);
          }

          for (const inspection of stepInspections) {
            await options.harness.emitProgress?.({
              phase: "inspection",
              status: "running",
              scenarioId: scenario.id,
              stepId: step.id || `step-${index + 1}`,
              title: "Running step inspection",
              message: inspection.code.slice(0, 500),
            });
            const inspectionResult = await context.inspect(inspection.code);
            const inspectionText = JSON.stringify(inspectionResult, null, 2);
            assertMatches(
              `Inspection for ${scenario.id} step ${index + 1}`,
              inspectionText,
              inspection.includes,
              inspection.excludes,
            );
            if (inspection.check) {
              await inspection.check({
                ...context,
                result: inspectionResult,
              });
            }
            inspectionResults.push(inspectionResult);
            await options.harness.emitProgress?.({
              phase: "inspection",
              status: "passed",
              scenarioId: scenario.id,
              stepId: step.id || `step-${index + 1}`,
              title: "Step inspection passed",
              message: inspectionText.slice(0, 800),
              data: inspectionResult,
            });
          }

          for (const check of stepChecks) {
            await options.harness.emitProgress?.({
              phase: "check",
              status: "running",
              scenarioId: scenario.id,
              stepId: step.id || `step-${index + 1}`,
              title: "Running custom check",
            });
            await check(context);
            await options.harness.emitProgress?.({
              phase: "check",
              status: "passed",
              scenarioId: scenario.id,
              stepId: step.id || `step-${index + 1}`,
              title: "Custom check passed",
            });
          }

          stepResults.push({
            id: step.id || `step-${index + 1}`,
            request: step.request,
            responseText: completed.responseText,
            terminalKind: completed.terminalKind,
            finalCode: completed.finalCode,
            actionSummary: completed.actionSummary,
            promptInteractions: completed.promptInteractions,
            inspectionResults,
            assertions,
            turnDir: completed.turnDir,
          });
        }

        const lastStep = stepResults[stepResults.length - 1];
        if (!lastStep) {
          throw new Error(
            `Scenario ${scenario.id} produced no completed steps`,
          );
        }

        const result: AgentEvalResult = {
          scenario,
          status: "passed",
          responseText: lastStep.responseText,
          terminalKind: lastStep.terminalKind,
          finalCode: lastStep.finalCode,
          actionSummary: lastStep.actionSummary,
          promptInteractions: lastStep.promptInteractions,
          verification:
            lastStep.inspectionResults.length <= 1
              ? (lastStep.inspectionResults[0] ?? null)
              : lastStep.inspectionResults,
          assertions: stepResults.flatMap((step) => step.assertions || []),
          tokenUsage: aggregateConversationTokenUsage(conversation),
          steps: stepResults,
          turnDir: conversation.artifactDir,
        };

        await writeJson(
          path.join(conversation.artifactDir, "result.json"),
          result,
        );
        await writeJson(
          path.join(conversation.artifactDir, "report.json"),
          result,
        );
        await writeFile(
          path.join(conversation.artifactDir, "REPORT.md"),
          buildResultReport(result),
        );
        await writeSessionLogReport({
          artifactDir: options.harness.artifactDir,
          conversation,
          result,
        });
        await options.harness.emitProgress?.({
          phase: "scenario",
          status: "passed",
          scenarioId: scenario.id,
          title: "Scenario passed",
          message: `${result.assertions?.filter((assertion) => assertion.status === "passed").length || 0}/${result.assertions?.length || 0} assertions`,
          data: result,
        });
        finalResult = result;
      } catch (error) {
        const failureMessage =
          error instanceof Error ? error.message : String(error);
        const failedTurn =
          conversation.logTurns?.[conversation.logTurns.length - 1];
        if (failedTurn && !failedTurn.completed) {
          failedTurn.error = failureMessage;
          const failedIteration = latestIterationLog(failedTurn);
          if (failedIteration && !failedIteration.responseText) {
            failedIteration.error = failureMessage;
          }
        }
        if (attempt < 2 && isTransientEvalError(error)) {
          await options.harness.closeConversation(conversation);
          continue;
        }
        const failed: AgentEvalResult = {
          scenario,
          status: "failed",
          responseText: "",
          terminalKind: "reply",
          actionSummary: [],
          promptInteractions: [],
          verification: null,
          tokenUsage: aggregateConversationTokenUsage(conversation),
          turnDir: path.join(options.harness.artifactDir, scenario.id),
          error: failureMessage,
        };
        await ensureDir(failed.turnDir);
        await writeJson(path.join(failed.turnDir, "result.json"), failed);
        await writeJson(path.join(failed.turnDir, "report.json"), failed);
        await writeFile(
          path.join(failed.turnDir, "REPORT.md"),
          buildResultReport(failed),
        );
        await writeSessionLogReport({
          artifactDir: options.harness.artifactDir,
          conversation,
          result: failed,
          error: failureMessage,
        });
        await options.harness.emitProgress?.({
          phase: "scenario",
          status: "failed",
          scenarioId: scenario.id,
          title: "Scenario failed",
          message: failureMessage,
          data: failed,
        });
        finalResult = failed;
      } finally {
        await options.harness.closeConversation(conversation);
      }
    }
    if (!finalResult) {
      finalResult = {
        scenario,
        status: "failed",
        responseText: "",
        terminalKind: "reply",
        actionSummary: [],
        promptInteractions: [],
        verification: null,
        tokenUsage: emptyTokenUsage(),
        turnDir: path.join(options.harness.artifactDir, scenario.id),
        error: "Scenario ended without a result.",
      };
    }
    results.push(finalResult);
  }

  await writeJson(
    path.join(options.harness.artifactDir, "summary.json"),
    results,
  );
  await writeFile(
    path.join(options.harness.artifactDir, "REPORT_INDEX.md"),
    buildSuiteIndex(results),
  );
  await ensureDir(path.join(options.harness.artifactDir, "logs"));
  await writeFile(
    path.join(options.harness.artifactDir, "logs", "README.md"),
    buildLogsIndex(results),
  );
  return { artifactDir: options.harness.artifactDir, results };
}

export function createAgentEvalHarness(options: AgentEvalHarnessOptions) {
  const artifactDir = buildArtifactDir(
    options.artifactBaseDir,
    options.suiteName,
  );
  const controllerBudgets = {
    ...DEFAULT_CONTROLLER_BUDGETS,
    ...(options.controllerBudgets || {}),
  };
  const chatTimeoutMs = options.chatTimeoutMs ?? 120_000;
  const jobTimeoutMs = options.jobTimeoutMs ?? 90_000;
  const pollIntervalMs = options.pollIntervalMs ?? 250;
  const isTruthyEnv = (value?: string): boolean => {
    return (
      value?.trim().toLowerCase() === "1" ||
      value?.trim().toLowerCase() === "true" ||
      value?.trim().toLowerCase() === "yes" ||
      value?.trim().toLowerCase() === "on"
    );
  };
  const localTurnReportsEnabled = (() => {
    if (typeof options.local === "boolean") return options.local;
    return (
      process.env.NODE_ENV === "development" ||
      isTruthyEnv(process.env.GRANULAR_LOCAL) ||
      isTruthyEnv(process.env.GRANULAR_USE_LOCAL_ENDPOINTS) ||
      isTruthyEnv(process.env.GRANULAR_USE_LOCAL) ||
      process.env.GRANULAR_ENDPOINT_MODE?.toLowerCase() === "local"
    );
  })();
  const localTurnReportBaseDir = path.resolve(
    options.localTurnReportBaseDir || process.cwd(),
  );
  const resolvedTemplate = resolveHarnessTemplate(
    options.harnessTemplateId ||
      process.env.GRANULAR_AGENT_HARNESS_TEMPLATE ||
      "stable",
    { strict: true },
  );
  const promptRenderer =
    options.promptRenderer || resolvedTemplate.renderPrompt;
  const continuationRenderer =
    options.continuationRenderer || resolvedTemplate.renderContinuation;
  const onProgress = options.onProgress;

  async function openConversation(
    label: string,
  ): Promise<AgentEvalConversation> {
    await ensureDir(artifactDir);
    const clientId = `${slugify(label)}-${Date.now()}`;
    if (!options.openEnvironment && !options.environmentId) {
      throw new Error(
        "createAgentEvalHarness requires either environmentId or openEnvironment",
      );
    }
    const environment = options.openEnvironment
      ? await options.openEnvironment({ label, clientId })
      : await options.granular.createSession({
          environmentId: options.environmentId!,
          clientId,
        });
    const promptEvents: PendingPromptEvent[] = [];
    const promptHandler = (payload: unknown) => {
      const prompt = payload as Prompt;
      promptEvents.push({ prompt, receivedAt: Date.now() });
    };
    environment.on("prompt", promptHandler);
    for (let attempt = 0; attempt < 12; attempt += 1) {
      if (environment.getEffects().length > 0) break;
      await sleep(250);
    }
    await waitForSessionDocumentContext(environment);
    await ensureDir(path.join(artifactDir, slugify(label)));
    return {
      label,
      environment,
      history: [],
      promptEvents,
      artifactDir: path.join(artifactDir, slugify(label)),
      turnCount: 0,
      logTurns: [],
    };
  }

  async function closeConversation(
    conversation: AgentEvalConversation,
  ): Promise<void> {
    try {
      await options.granular.closeSession(
        conversation.environment.sessionId,
        conversation.environment,
      );
    } catch {
      // Best-effort cleanup for eval conversations. Teardown failures should not
      // overwrite the actual scenario result.
    }
  }

  async function writeLocalTurnReport(input: {
    status: "completed" | "pending" | "failed";
    conversation: AgentEvalConversation;
    turnLog: AgentEvalSessionLogTurn;
    request: string;
    requestTimestamp: number;
    completed?: AgentEvalCompletedTurn;
    pending?: AgentEvalPendingTurn;
    error?: string;
  }) {
    if (!localTurnReportsEnabled) return;
    const { status, conversation, turnLog, request, requestTimestamp, error } =
      input;
    const requestId = safePathSegment(
      new Date(requestTimestamp).toISOString().replace(/[:.]/g, "-"),
      "turn",
    );
    const reportDir = path.join(
      localTurnReportBaseDir,
      safePathSegment(conversation.environment.ontologyId, "ontology"),
      safePathSegment(conversation.environment.subjectId, "subject"),
      safePathSegment(conversation.environment.sessionId, "session"),
    );
    await ensureDir(reportDir);

    const completed = input.completed;
    const pending = input.pending;
    const report = buildTurnMdxReport({
      status,
      conversation,
      request,
      requestTimestamp,
      turnLog,
      responseText: completed?.responseText,
      terminalKind: completed?.terminalKind,
      actionSummary: completed?.actionSummary,
      promptInteractions:
        completed?.promptInteractions || pending?.promptInteractions,
      result: completed?.result,
      prompts: pending?.prompts,
      verification: completed?.verification,
      error,
    });
    const reportPath = path.join(reportDir, `${requestId}.mdx`);
    await writeFile(reportPath, report);
    console.log(`[agent][local] saved query report: ${reportPath}`);
  }

  async function runCheckJob(
    code: string,
    session: EnvironmentSession,
  ): Promise<unknown> {
    const job = await session.submitJob(code);
    return withTimeout(job.result, jobTimeoutMs, `check job ${job.id}`);
  }

  function buildCheckContext(
    conversation: AgentEvalConversation,
    completed: AgentEvalCompletedTurn,
    turnDir: string,
  ): AgentEvalCheckContext {
    const liveDoc = cloneJson(conversation.environment.document) as Record<
      string,
      unknown
    >;
    const heap = normalizeHeapSnapshot(asRecord(liveDoc?.heap));
    return {
      conversation,
      environment: conversation.environment,
      turnDir,
      request: completed.request,
      responseText: completed.responseText,
      terminalKind: completed.terminalKind,
      finalCode: completed.finalCode,
      actionSummary: completed.actionSummary,
      promptInteractions: completed.promptInteractions,
      result: completed.result,
      heap,
      openPrompts: getOpenPromptsFromDoc(liveDoc),
      liveDoc,
      inspect: (code: string) => runCheckJob(code, conversation.environment),
      assertMatches,
    };
  }

  async function runInspection(
    conversation: AgentEvalConversation,
    inspection: AgentEvalInspection,
    completed: AgentEvalCompletedTurn,
    turnDir: string,
  ): Promise<unknown> {
    await emitProgress(onProgress, {
      phase: "inspection",
      status: "running",
      scenarioId: conversation.label,
      title: "Running inspection",
      message: inspection.code.slice(0, 500),
    });
    let result: unknown = null;
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 10; attempt += 1) {
      result = await runCheckJob(inspection.code, conversation.environment);
      const text = JSON.stringify(result, null, 2);
      try {
        assertMatches(
          `Verification for ${conversation.label}`,
          text,
          inspection.includes,
          inspection.excludes,
        );
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
        await sleep(250);
      }
    }
    if (lastError) throw lastError;
    if (inspection.check) {
      await inspection.check({
        ...buildCheckContext(conversation, completed, turnDir),
        result,
      });
    }
    await writeJson(path.join(turnDir, "verification.json"), result);
    await emitProgress(onProgress, {
      phase: "inspection",
      status: "passed",
      scenarioId: conversation.label,
      title: "Inspection passed",
      message: JSON.stringify(result)?.slice(0, 800),
      data: result,
    });
    return result;
  }

  async function resumePendingTurn(
    pending: AgentEvalPendingTurn,
    responder: AgentEvalPromptResponder,
  ): Promise<AgentEvalCompletedTurn | AgentEvalPendingTurn> {
    const prompt = pending.prompts[0];
    if (!prompt) throw new Error("Pending turn has no prompts to answer");
    const answer = await responder({
      prompt,
      history: pending.promptInteractions,
    });
    await emitProgress(onProgress, {
      phase: "interaction",
      status: "running",
      scenarioId: pending.conversation.label,
      stepId: path.basename(pending.turnDir),
      jobId: pending.job.id,
      title: "Prompt answered",
      message: `${prompt.type}: ${prompt.message || prompt.title} -> ${JSON.stringify(answer)}`,
      data: { prompt, answer },
    });
    const session = pending.conversation.environment;
    await session.answerPrompt(prompt.id, answer);
    pending.promptInteractions.push({
      promptId: prompt.id,
      type: prompt.type,
      title: prompt.title,
      message: prompt.message,
      answer,
    });
    const turnLog = findTurnLog(pending.conversation, pending.turnDir);
    const iterationLog = latestIterationLog(turnLog);
    if (iterationLog) {
      iterationLog.promptInteractions = pending.promptInteractions;
    }

    const resumed = await waitForJobOutcome({
      environment: pending.conversation.environment,
      job: pending.job,
      boundaryTimestamp: pending.boundaryTimestamp,
      timeoutMs: jobTimeoutMs,
      pollIntervalMs,
      onProgress: (event) => void onProgress?.(event),
      progressContext: {
        scenarioId: pending.conversation.label,
        stepId: path.basename(pending.turnDir),
      },
    });

    if (resumed.kind === "prompt") {
      return {
        ...pending,
        prompts: resumed.prompts,
        stdout: [...pending.stdout, ...resumed.stdout],
        stderr: [...pending.stderr, ...resumed.stderr],
      };
    }

    await sleep(350);

    const liveDoc = cloneJson(
      pending.conversation.environment.document,
    ) as Record<string, unknown>;
    const presentation = resolveJobPresentation({
      jobId: pending.job.id,
      result: resumed.result,
      feedItems: await collectJobFeedItems(
        pending.conversation.environment,
        pending.job.id,
      ),
      sessionHeap: normalizeHeapSnapshot(asRecord(liveDoc?.heap)),
    });
    const responseText = presentation.responseText ?? "";
    pending.conversation.history.push({
      role: "assistant",
      content: responseText,
      code: pending.finalCode,
      jobStatus: "succeeded",
      jobResultPreview: JSON.stringify(resumed.result, null, 2),
    });

    await writeJson(path.join(pending.turnDir, "result.json"), {
      responseText,
      terminalKind: getCurrentClosureId(liveDoc) ? "closure" : "reply",
      actionSummary: getActionSummary(liveDoc, pending.job.id),
      promptInteractions: pending.promptInteractions,
      result: resumed.result,
    });
    const actionSummary = getActionSummary(liveDoc, pending.job.id);
    if (iterationLog) {
      iterationLog.responseText = responseText;
      iterationLog.terminalKind = getCurrentClosureId(liveDoc)
        ? "closure"
        : "reply";
      iterationLog.actionSummary = actionSummary;
      iterationLog.promptInteractions = pending.promptInteractions;
      iterationLog.result = resumed.result;
    }
    if (turnLog) {
      turnLog.completed = {
        responseText,
        terminalKind: getCurrentClosureId(liveDoc) ? "closure" : "reply",
        actionSummary,
        promptInteractions: pending.promptInteractions,
        result: resumed.result,
      };
    }

    return {
      conversation: pending.conversation,
      request: pending.request,
      turnDir: pending.turnDir,
      responseText,
      terminalKind: getCurrentClosureId(liveDoc) ? "closure" : "reply",
      finalCode: pending.finalCode,
      actionSummary,
      promptInteractions: pending.promptInteractions,
      verification: null,
      result: resumed.result,
    };
  }

  async function runTurn(input: {
    conversation: AgentEvalConversation;
    request: string;
    prepareRecords?: RecordObjectOptions[];
    prepareTools?: ToolWithHandler[];
    prepare?: (ctx: AgentEvalPrepareContext) => Promise<void>;
    human?: AgentEvalPromptResponder;
    verification?: AgentEvalInspection;
    maxIterations?: number;
    autoAnswerPrompts?: boolean;
    uiContext?: Record<string, unknown>;
  }): Promise<AgentEvalCompletedTurn | AgentEvalPendingTurn> {
    const conversation = input.conversation;
    const turnNumber = ++conversation.turnCount;
    const turnId = `turn-${String(turnNumber).padStart(2, "0")}-${slugify(input.request.slice(0, 48))}`;
    const turnDir = path.join(conversation.artifactDir, turnId);
    await ensureDir(turnDir);
    const turnLog: AgentEvalSessionLogTurn = {
      turnNumber,
      turnId,
      request: input.request,
      turnDir,
      iterations: [],
    };
    conversation.logTurns.push(turnLog);

    if (input.prepareRecords?.length) {
      await emitProgress(onProgress, {
        phase: "setup",
        status: "running",
        scenarioId: conversation.label,
        stepId: turnId,
        title: "Recording setup records",
        message: `${input.prepareRecords.length} records`,
        data: input.prepareRecords,
      });
      await conversation.environment.recordObjects(input.prepareRecords);
      await emitProgress(onProgress, {
        phase: "setup",
        status: "passed",
        scenarioId: conversation.label,
        stepId: turnId,
        title: "Setup records recorded",
        message: `${input.prepareRecords.length} records`,
      });
    }
    if (input.prepareTools?.length) {
      await emitProgress(onProgress, {
        phase: "setup",
        status: "running",
        scenarioId: conversation.label,
        stepId: turnId,
        title: "Registering effect handlers",
        message: `${input.prepareTools.length} handlers`,
      });
      await options.granular
        .ontology(conversation.environment.sandboxId)
        .effects.registerMany(input.prepareTools);
    }
    if (input.prepare) {
      await emitProgress(onProgress, {
        phase: "setup",
        status: "running",
        scenarioId: conversation.label,
        stepId: turnId,
        title: "Running custom setup",
      });
      await input.prepare({
        conversation,
        environment: conversation.environment,
        turnDir,
      });
    }

    const boundaryTimestamp = Date.now();
    conversation.history.push({ role: "user", content: input.request });
    await emitProgress(onProgress, {
      phase: "step",
      status: "running",
      scenarioId: conversation.label,
      stepId: turnId,
      title: "User request",
      message: input.request,
    });
    await writeJson(path.join(turnDir, "request.json"), {
      request: input.request,
      boundaryTimestamp,
    });

    let iteration = 0;
    let noProgressCount = 0;
    let previousSnapshot: HarnessVerifierSnapshot | null = null;
    let latestCheckpoint: GranularAgentExecutionCheckpoint | null = null;
    const maxIterations =
      input.maxIterations || controllerBudgets.maxIterations;
    const autoAnswerPrompts = input.autoAnswerPrompts ?? true;
    const baselineClosureId = getCurrentClosureId(
      cloneJson(conversation.environment.document) as Record<string, unknown>,
    );
    const writeTurnReport = async (status: {
      type: "completed" | "pending" | "failed";
      completed?: AgentEvalCompletedTurn;
      pending?: AgentEvalPendingTurn;
      error?: string;
    }) => {
      await writeLocalTurnReport({
        status: status.type,
        conversation,
        turnLog,
        request: input.request,
        requestTimestamp: boundaryTimestamp,
        completed: status.completed,
        pending: status.pending,
        error: status.error,
      });
    };

    try {
      while (iteration < maxIterations) {
        const liveDoc = cloneJson(conversation.environment.document) as Record<
          string,
          unknown
        >;
        const promptHeap = normalizeHeapSnapshot(asRecord(liveDoc?.heap));
        const pendingPrompts = filterPromptsByBoundary(
          liveDoc,
          getOpenPromptsFromDoc(liveDoc),
          boundaryTimestamp,
        );
        const workflowFocus = projectWorkflowFocus(liveDoc, pendingPrompts, {
          boundaryTimestamp,
        });
        const referentFocus = projectConversationReferentFocus(liveDoc);
        const heapFocus = {
          variableNames: [
            ...workflowFocus.variableNames,
            ...referentFocus.variableNames,
          ],
          listNames: [...workflowFocus.listNames, ...referentFocus.listNames],
          entryPaths: [
            ...workflowFocus.entryPaths,
            ...referentFocus.entryPaths,
            ...focusedHeapEntryPathsFromUiContext(promptHeap, input.uiContext),
          ],
        };
        const tools = conversation.environment.getEffects().map((tool) => ({
          name: tool.name,
          description: tool.description,
          className: tool.className,
          static: tool.static,
          ready: tool.ready,
          inputSchema: tool.inputSchema,
          outputSchema: tool.outputSchema,
        }));
        const renderedPrompt = promptRenderer({
          domainDocumentation:
            await conversation.environment.getDomainDocumentation(),
          sessionContext: {
            sandboxId: conversation.environment.sandboxId,
            environmentId: conversation.environment.environmentId,
            domainRevision: conversation.environment.domainRevision,
            uiContext: input.uiContext || null,
          },
          heapSummary: projectHeapSummary(asRecord(liveDoc?.heap), {
            focus: heapFocus,
          }),
          fileSummary: projectSessionFileSummary(liveDoc),
          referentSummary: projectConversationReferentSummary(liveDoc),
          loopSummary: projectLoopSummary(liveDoc, pendingPrompts, {
            boundaryTimestamp,
          }),
          workflowSummary: projectWorkflowSummary(liveDoc, pendingPrompts, {
            boundaryTimestamp,
          }),
          tools,
          checkpoint: latestCheckpoint,
        });
        const systemPrompt = renderedPrompt.prompt;
        await emitProgress(onProgress, {
          phase: "prompt",
          status: "passed",
          scenarioId: conversation.label,
          stepId: turnId,
          iteration: iteration + 1,
          templateId: renderedPrompt.templateId,
          templateVersion: renderedPrompt.templateVersion,
          title: "Rendered harness prompt",
          message: `${systemPrompt.split("\n").length} lines`,
          data: {
            templateId: renderedPrompt.templateId,
            templateVersion: renderedPrompt.templateVersion,
            templateHash: renderedPrompt.templateHash,
            promptInstanceHash: renderedPrompt.promptInstanceHash,
            prompt: systemPrompt,
          },
        });

        const request =
          iteration === 0
            ? input.request
            : continuationRenderer(
                buildContinuationPreview(latestCheckpoint, noProgressCount),
              ).instruction;

        await emitProgress(onProgress, {
          phase: "generation",
          status: "running",
          scenarioId: conversation.label,
          stepId: turnId,
          iteration: iteration + 1,
          templateId: renderedPrompt.templateId,
          templateVersion: renderedPrompt.templateVersion,
          title:
            iteration === 0
              ? "Generating agent response"
              : "Generating continuation",
          message: request,
        });
        const emittedGeneratedReasoningLines = new Set<string>();
        let generatedReasoningBuffer = "";
        const emitGeneratedReasoningLine = async (line: string) => {
          if (emittedGeneratedReasoningLines.has(line)) return;
          emittedGeneratedReasoningLines.add(line);
          await emitProgress(onProgress, {
            phase: "generation",
            status: "running",
            scenarioId: conversation.label,
            stepId: turnId,
            iteration: iteration + 1,
            templateId: renderedPrompt.templateId,
            templateVersion: renderedPrompt.templateVersion,
            title: "Generated reasoning comment",
            message: line,
            data: { source: "generated_code_comment" },
          });
        };
        const generationStartedAt = Date.now();
        const generation = await withTimeout(
          generateTurnWithRepair(options.generator, {
            systemPrompt,
            history: buildHistory(conversation.history),
            request,
            attempt: 1,
            tools,
            onReplyDelta: async (delta) => {
              await emitProgress(onProgress, {
                phase: "generation",
                status: "running",
                scenarioId: conversation.label,
                stepId: turnId,
                iteration: iteration + 1,
                templateId: renderedPrompt.templateId,
                templateVersion: renderedPrompt.templateVersion,
                title: "Generated text reply delta",
                message: delta,
                data: { delta },
              });
            },
            onCodeDelta: async (delta) => {
              const parsed = consumeGranularReasoningOnlyChunk(
                generatedReasoningBuffer,
                delta,
              );
              generatedReasoningBuffer = parsed.buffer;
              for (const line of parsed.reasoningLines) {
                await emitGeneratedReasoningLine(line);
              }
            },
            usageContext: {
              sandboxId: conversation.environment.sandboxId,
              environmentId: conversation.environment.environmentId,
              sessionId: conversation.environment.sessionId,
              subjectId: conversation.environment.subjectId,
              permissionProfileId: conversation.environment.permissionProfileId,
            },
          }),
          chatTimeoutMs,
          `chat generation for ${conversation.label} iteration ${iteration + 1}`,
        );
        const generationDurationMs = Date.now() - generationStartedAt;
        await emitProgress(onProgress, {
          phase: "generation",
          status: "passed",
          scenarioId: conversation.label,
          stepId: turnId,
          iteration: iteration + 1,
          templateId: renderedPrompt.templateId,
          templateVersion: renderedPrompt.templateVersion,
          title: generation.code
            ? "Generated job code"
            : "Generated text reply",
          message: generation.code || generation.reply || "",
          data: {
            reply: generation.reply,
            code: generation.code,
            attempts: generation.generationAttempts,
            usage: tokenUsageForGenerationOutput(generation),
          },
        });
        const generatedReasoningLines = generation.code
          ? consumeGranularReasoningOnlyChunk("", generation.code, {
              final: true,
            }).reasoningLines
          : [];
        for (const line of generatedReasoningLines) {
          await emitGeneratedReasoningLine(line);
        }
        const iterationLog: AgentEvalSessionLogIteration = {
          iteration: iteration + 1,
          request,
          generationDurationMs,
          systemPrompt,
          templateId: renderedPrompt.templateId,
          templateVersion: renderedPrompt.templateVersion,
          templateHash: renderedPrompt.templateHash,
          promptInstanceHash: renderedPrompt.promptInstanceHash,
          generationReply: generation.reply,
          generatedCode: generation.code,
          rawGeneration: generation.raw,
          generationAttempts: generation.generationAttempts,
          tokenUsage: tokenUsageForGenerationOutput(generation),
        };
        turnLog.iterations.push(iterationLog);

        await writeJson(
          path.join(turnDir, `iteration-${iteration + 1}-generation.json`),
          generation,
        );

        if (!generation.code) {
          const responseText = "";
          const completed: AgentEvalCompletedTurn = {
            conversation,
            request: input.request,
            turnDir,
            responseText,
            terminalKind: "reply",
            actionSummary: [],
            promptInteractions: [],
            verification: null,
            result: {
              status: "no_job_submitted",
              canonicalReplyAuthored: false,
            },
          };
          if (input.verification) {
            completed.verification = await runInspection(
              conversation,
              input.verification,
              completed,
              turnDir,
            );
          }
          iterationLog.responseText = responseText;
          iterationLog.terminalKind = "reply";
          iterationLog.actionSummary = [];
          iterationLog.promptInteractions = [];
          iterationLog.result = completed.result;
          turnLog.completed = {
            responseText,
            terminalKind: "reply",
            actionSummary: [],
            promptInteractions: [],
            result: completed.result,
          };
          await writeJson(path.join(turnDir, "result.json"), completed);
          await writeTurnReport({ type: "completed", completed });
          await emitProgress(onProgress, {
            phase: "step",
            status: "passed",
            scenarioId: conversation.label,
            stepId: turnId,
            title: "Step completed without a canonical reply",
            message:
              "The generator submitted no job, so no customer-visible reply was authored.",
            data: completed,
          });
          return completed;
        }

        const session = conversation.environment;
        const job = await session.submitJob(generation.code, {
          agent: {
            userRequest: input.request,
            generationRequest: request,
            systemPrompt,
            history: buildHistory(conversation.history),
            scenarioLabel: conversation.label,
            turnId,
            iteration: iteration + 1,
            tools,
            generationReply: generation.reply,
            rawGeneration: generation.raw,
            repairIssues: generation.generationAttempts?.flatMap(
              (attempt) => attempt.repairIssues || [],
            ),
          },
        });
        await emitProgress(onProgress, {
          phase: "job",
          status: "running",
          scenarioId: conversation.label,
          stepId: turnId,
          iteration: iteration + 1,
          jobId: job.id,
          templateId: renderedPrompt.templateId,
          templateVersion: renderedPrompt.templateVersion,
          title: "Submitted Granular job",
          message: job.id,
          data: { code: generation.code },
        });
        const outcome = await waitForJobOutcome({
          environment: conversation.environment,
          job,
          boundaryTimestamp,
          timeoutMs: jobTimeoutMs,
          pollIntervalMs,
          onProgress: (event) => void onProgress?.(event),
          progressContext: {
            scenarioId: conversation.label,
            stepId: turnId,
            iteration: iteration + 1,
            templateId: renderedPrompt.templateId,
            templateVersion: renderedPrompt.templateVersion,
          },
        });

        if (outcome.kind === "prompt") {
          if (!autoAnswerPrompts) {
            const pending: AgentEvalPendingTurn = {
              conversation,
              request: input.request,
              turnDir,
              boundaryTimestamp,
              finalCode: generation.code,
              stdout: outcome.stdout,
              stderr: outcome.stderr,
              prompts: outcome.prompts,
              promptInteractions: [],
              job,
            };
            await writeTurnReport({ type: "pending", pending });
            return pending;
          }
          if (!input.human) {
            throw new Error(
              "This turn reached a human prompt but no responder was provided",
            );
          }
          let pending: AgentEvalPendingTurn = {
            conversation,
            request: input.request,
            turnDir,
            boundaryTimestamp,
            finalCode: generation.code,
            stdout: outcome.stdout,
            stderr: outcome.stderr,
            prompts: outcome.prompts,
            promptInteractions: [],
            job,
          };

          while ("prompts" in pending) {
            const resumed = await resumePendingTurn(pending, input.human);
            if ("prompts" in resumed) {
              pending = resumed;
              continue;
            }
            if (input.verification) {
              resumed.verification = await runInspection(
                conversation,
                input.verification,
                resumed,
                turnDir,
              );
            }
            turnLog.completed = {
              responseText: resumed.responseText,
              terminalKind: resumed.terminalKind,
              actionSummary: resumed.actionSummary,
              promptInteractions: resumed.promptInteractions,
              result: resumed.result,
            };
            await writeTurnReport({ type: "completed", completed: resumed });
            return resumed;
          }
        }

        if (outcome.kind !== "completed") {
          throw new Error(
            "Unexpected non-completed outcome after prompt handling",
          );
        }

        await sleep(350);

        const settledLiveDoc = cloneJson(
          conversation.environment.document,
        ) as Record<string, unknown>;
        const sessionHeap = normalizeHeapSnapshot(
          asRecord(settledLiveDoc?.heap),
        );
        const presentation = resolveJobPresentation({
          jobId: job.id,
          result: outcome.result,
          feedItems: await collectJobFeedItems(
            conversation.environment,
            job.id,
          ),
          sessionHeap,
        });
        const responseText = presentation.responseText ?? "";
        const verifierSnapshot = createHarnessVerifierSnapshot({
          finalCode: generation.code,
          resultPreview: JSON.stringify(outcome.result, null, 2),
          liveDoc: settledLiveDoc,
          projectionOptions: { boundaryTimestamp },
        });

        const continuation = evaluateContinuation({
          iteration,
          budgets: controllerBudgets,
          baselineClosureId,
          currentClosureId: getCurrentClosureId(settledLiveDoc),
          liveDoc: settledLiveDoc,
          pendingPrompts: filterPromptsByBoundary(
            settledLiveDoc,
            getOpenPromptsFromDoc(settledLiveDoc),
            boundaryTimestamp,
          ),
          projectionOptions: { boundaryTimestamp },
          latestResponseText: responseText,
          previousSnapshot,
          currentSnapshot: verifierSnapshot,
          previousNoProgressCount: noProgressCount,
        });

        latestCheckpoint = {
          iteration: iteration + 1,
          latestJobStatus: "succeeded",
          latestJobResult: JSON.stringify(outcome.result, null, 2),
          latestActionSummary: getActionSummary(settledLiveDoc, job.id),
          controllerOutcome: continuation.outcome,
          controllerReason: continuation.reason,
          noProgressCount: continuation.nextNoProgressCount,
        };
        await emitProgress(onProgress, {
          phase: "continuation",
          status: continuation.shouldContinue ? "running" : "passed",
          scenarioId: conversation.label,
          stepId: turnId,
          iteration: iteration + 1,
          jobId: job.id,
          templateId: renderedPrompt.templateId,
          templateVersion: renderedPrompt.templateVersion,
          title: continuation.shouldContinue
            ? "Harness requested another loop"
            : "Harness accepted completion",
          message: `${continuation.reason}; ${continuation.outcome}`,
          data: {
            continuation,
            checkpoint: latestCheckpoint,
            verifierSnapshot,
          },
        });

        previousSnapshot = verifierSnapshot;
        noProgressCount = continuation.nextNoProgressCount;

        conversation.history.push({
          role: "assistant",
          content: responseText,
          code: generation.code,
          jobStatus: "succeeded",
          jobResultPreview: JSON.stringify(outcome.result, null, 2),
        });

        await writeJson(
          path.join(turnDir, `iteration-${iteration + 1}-result.json`),
          {
            responseText,
            continuation,
            actionSummary: latestCheckpoint.latestActionSummary,
            result: outcome.result,
          },
        );
        iterationLog.responseText = responseText;
        iterationLog.terminalKind = getCurrentClosureId(settledLiveDoc)
          ? "closure"
          : "reply";
        iterationLog.actionSummary = latestCheckpoint.latestActionSummary || [];
        iterationLog.promptInteractions = [];
        iterationLog.continuation = continuation;
        iterationLog.result = outcome.result;

        if (!continuation.shouldContinue) {
          const completed: AgentEvalCompletedTurn = {
            conversation,
            request: input.request,
            turnDir,
            responseText,
            terminalKind: getCurrentClosureId(settledLiveDoc)
              ? "closure"
              : "reply",
            finalCode: generation.code,
            actionSummary: latestCheckpoint.latestActionSummary || [],
            promptInteractions: [],
            verification: null,
            result: outcome.result,
          };
          if (input.verification) {
            completed.verification = await runInspection(
              conversation,
              input.verification,
              completed,
              turnDir,
            );
          }
          turnLog.completed = {
            responseText,
            terminalKind: completed.terminalKind,
            actionSummary: completed.actionSummary,
            promptInteractions: [],
            result: outcome.result,
          };
          await writeJson(path.join(turnDir, "result.json"), completed);
          await writeTurnReport({ type: "completed", completed });
          await emitProgress(onProgress, {
            phase: "step",
            status: "passed",
            scenarioId: conversation.label,
            stepId: turnId,
            iteration: iteration + 1,
            jobId: job.id,
            title: "Step completed",
            message: responseText,
            data: completed,
          });
          return completed;
        }

        iteration += 1;
      }
      throw new Error(
        `Turn exceeded iteration budget (${maxIterations}) for ${conversation.label}`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const latestIteration = latestIterationLog(turnLog);
      if (latestIteration) {
        latestIteration.error = message;
      }
      turnLog.error = message;
      await writeTurnReport({ type: "failed", error: message });
      throw error;
    }
  }

  return {
    artifactDir,
    granular: options.granular,
    emitProgress: (
      event: Omit<AgentEvalProgressEvent, "id" | "timestamp"> & {
        id?: string;
      },
    ) => emitProgress(onProgress, event),
    openConversation,
    closeConversation,
    runTurn,
    resumePendingTurn,
  };
}

export type AgentTestTarget =
  | { environmentId: string }
  | { connect: ConnectOptions }
  | { createEnvironment: { sandboxId: string; data: CreateEnvironmentData } };

export interface AgentTesterOptions {
  granular?: Granular;
  client?: Granular;
  apiKey?: string;
  token?: string;
  apiUrl?: string;
  local?: boolean;
  granularOptions?: Omit<
    GranularOptions,
    "apiKey" | "token" | "apiUrl" | "endpointMode"
  >;
  generator?: AgentEvalTurnGenerator;
  openai?: {
    apiKey: string;
    model?: string;
    baseUrl?: string;
    temperature?: number;
    reasoningEffort?: "low" | "medium" | "high";
    headers?: Record<string, string>;
  };
  model?: string;
  target: AgentTestTarget;
  artifactBaseDir?: string;
  suiteName?: string;
  controllerBudgets?: HarnessControllerBudgets;
  chatTimeoutMs?: number;
  jobTimeoutMs?: number;
  pollIntervalMs?: number;
  harnessTemplateId?: string;
  promptRenderer?: AgentEvalPromptRenderer;
  continuationRenderer?: AgentEvalContinuationRenderer;
  onProgress?: (event: AgentEvalProgressEvent) => void | Promise<void>;
}

export function createAgentTester(options: AgentTesterOptions) {
  const granular =
    options.client ||
    options.granular ||
    new Granular({
      apiKey: options.apiKey,
      token: options.token,
      apiUrl: options.apiUrl,
      endpointMode: options.local ? "local" : "production",
      ...(options.granularOptions || {}),
    });

  const generator =
    options.generator ||
    createOpenAIChatTurnGenerator({
      apiKey: options.openai?.apiKey || "",
      model: options.openai?.model || options.model,
      baseUrl: options.openai?.baseUrl,
      temperature: options.openai?.temperature,
      reasoningEffort: options.openai?.reasoningEffort,
      headers: options.openai?.headers,
      onUsage: async (usage) => {
        await granular.recordOpenAIUsageSpend(usage, usage.usageContext);
      },
    });
  let resolvedEnvironmentId =
    "environmentId" in options.target ? options.target.environmentId : null;
  let connectSeeded = false;

  const harness = createAgentEvalHarness({
    granular,
    environmentId: resolvedEnvironmentId || undefined,
    local: options.local,
    openEnvironment: async ({ clientId }) => {
      if (resolvedEnvironmentId) {
        return granular.createSession({
          environmentId: resolvedEnvironmentId,
          clientId,
        });
      }

      if ("connect" in options.target && !connectSeeded) {
        connectSeeded = true;
        const environment = await granular.openEnvironment({
          ...options.target.connect,
        });
        resolvedEnvironmentId = environment.environmentId;
        return environment.sessions.create({ clientId });
      }

      if ("createEnvironment" in options.target && !resolvedEnvironmentId) {
        const envData = await granular.environments.create(
          options.target.createEnvironment.sandboxId,
          options.target.createEnvironment.data,
        );
        resolvedEnvironmentId = envData.environmentId;
      }

      if (!resolvedEnvironmentId) {
        throw new Error("Agent tester could not resolve an environmentId");
      }

      return granular.createSession({
        environmentId: resolvedEnvironmentId,
        clientId,
      });
    },
    generator,
    artifactBaseDir: options.artifactBaseDir,
    suiteName: options.suiteName,
    controllerBudgets: options.controllerBudgets,
    chatTimeoutMs: options.chatTimeoutMs,
    jobTimeoutMs: options.jobTimeoutMs,
    pollIntervalMs: options.pollIntervalMs,
    harnessTemplateId: options.harnessTemplateId,
    promptRenderer: options.promptRenderer,
    continuationRenderer: options.continuationRenderer,
    onProgress: options.onProgress,
  });

  return {
    ...harness,
    granular,
    generator,
    get environmentId() {
      return resolvedEnvironmentId;
    },
    openThread: harness.openConversation,
    closeThread: harness.closeConversation,
    runStep: harness.runTurn,
    continueStep: harness.resumePendingTurn,
    runSuite: (scenarios: AgentEvalScenario[]) =>
      runAgentEvalSuite({ harness, scenarios }),
  };
}

export async function runAgentTests(options: {
  tester:
    | ReturnType<typeof createAgentTester>
    | ReturnType<typeof createAgentEvalHarness>;
  scenarios: AgentEvalScenario[];
}): Promise<AgentEvalSuiteResult> {
  return runAgentEvalSuite({
    harness: options.tester as ReturnType<typeof createAgentEvalHarness>,
    scenarios: options.scenarios,
  });
}

export const createHumanResponder = createScriptedPromptResponder;
export const createOpenAIGenerator = createOpenAIChatTurnGenerator;
export const createTestArtifactsDirectory = createTimestampedArtifactDirectory;
